import { oc } from "@orpc/contract";
import { z } from "zod";
import { FileStreamSchema } from "../../schemas/common";
import {
  RD_DC_CROSS_REFERENCES,
  itemMasterLetterForRdStock,
  stockLetterForRdItemMaster,
} from "./rd-branch-correspondence";

// ─── Schemas ────────────────────────────────────────────────────────────────

export const ItemCatalogItemSchema = z.object({
  // The product's explicit, caller-provided string primary key (ProductV3.key).
  key: z.string(),
  displaySku: z.string().nullable(),
  displayDescription: z.string().nullable(),
  /**
   * Product-level UPC from the registered IM_PRODUCT_V3 schema extension
   * (`upc` field). Null when unset or when the org has not provisioned one.
   */
  upc: z.string().nullable(),
  isSellable: z.boolean(),
  isActive: z.boolean(),
  updatedAt: z.string(),
});

export const ItemCatalogFilterSchema = z.object({
  search: z.string().trim().optional(),
  // "text" (default) matches the term as a case-insensitive substring
  // (ILIKE %term%); "regex" matches it as a POSIX regular expression (~*).
  searchMode: z.enum(["text", "regex"]).default("text"),
  isSellable: z.coerce.boolean().optional(),
  isActive: z.coerce.boolean().optional(),
  sortDirection: z.enum(["asc", "desc"]).optional(),
});

export type ItemCatalogSearchMode = z.infer<
  typeof ItemCatalogFilterSchema
>["searchMode"];

// ─── Advanced filters ─────────────────────────────────────────────────────────
// Repeatable (column, operator, value) constraints, ANDed together. `column` is
// either a base ProductV3 column (see CATALOG_BASE_FILTER_COLUMNS) — filtered
// directly on im_products_v3 — or any linked raw metadata column, resolved
// against the ingest tables. String operators run case-insensitively; the two
// `is_empty` variants ignore `value`. `greater_than`/`less_than` compare
// numerically when the value is a number (metadata columns only — base text
// columns always compare lexicographically) and lexicographically otherwise.
export const CatalogFilterOperatorSchema = z.enum([
  "equals",
  "not_equals",
  "contains",
  "not_contains",
  "starts_with",
  "ends_with",
  "greater_than",
  "less_than",
  "like",
  "is_empty",
  "is_not_empty",
]);
export type CatalogFilterOperator = z.infer<typeof CatalogFilterOperatorSchema>;

// Operators that don't use `value` — the UI hides the value input for these and
// the backend ignores whatever is sent.
export const CATALOG_VALUELESS_OPERATORS: ReadonlySet<CatalogFilterOperator> =
  new Set(["is_empty", "is_not_empty"]);

export const CatalogAdvancedFilterSchema = z.object({
  column: z.string().trim().min(1),
  operator: CatalogFilterOperatorSchema,
  value: z.string().default(""),
});
export type CatalogAdvancedFilter = z.infer<typeof CatalogAdvancedFilterSchema>;

/**
 * How a set of raw-metadata filters has to be satisfied.
 *
 * - `row` (the default, and what the catalog has always done): every filter must
 *   be met by **one** ingest record. Only tables carrying all of the filtered
 *   columns are scanned at all, so a set spanning two AS/400 files — `ISTAT`
 *   lives on `as_item`, `IOWRN1` on `as_purmastd` — matches nothing.
 * - `product`: the filters are met by the **product**, treating its linked
 *   records as extensions of the one Gently item. Filters are grouped so that
 *   ones sharing a source file still have to be met by a single record of it
 *   (two vendor rows can't each satisfy half of a `as_purmastd` pair), and the
 *   groups are then ANDed across the product's links **at the same branch**:
 *   the records satisfying each group must share a CDB key (company, division
 *   and location), so an `as_item` match in QE's branch H never pairs with an
 *   `as_purmastd` match in QO's. Only comparable branches constrain each other
 *   — stocking-location records (`as_stock`, `as_prsls`, `as_miitm`) letter
 *   their branches from a different code space and cohere among themselves,
 *   and a record with no comparable branch at all (`as_gscost`, `as_pcovdet`,
 *   a blank branch cell) satisfies its group wherever it sits (see
 *   `AS400_LOCATION_SPACE_BY_SCHEMA`). A product with no record at all in a
 *   group's tables fails it.
 *
 * A filter set that resolves to a single group behaves identically under both,
 * which is what makes this safe to flip on an existing saved query.
 */
export const CatalogFilterMatchSchema = z.enum(["row", "product"]);
export type CatalogFilterMatch = z.infer<typeof CatalogFilterMatchSchema>;

export const CATALOG_FILTER_MATCH_DEFAULT: CatalogFilterMatch = "row";

/**
 * The filter-match field, on every endpoint that takes `advancedFilters`.
 *
 * Optional rather than defaulted so that omitting it stays omitted in the
 * inferred type: every existing caller means row-level matching, and each
 * reader treats an absent value as {@link CATALOG_FILTER_MATCH_DEFAULT}.
 */
export const catalogFilterMatchField = {
  advancedFilterMatch: CatalogFilterMatchSchema.optional(),
};

// User-facing catalog columns that live in the app DB (filtered without an
// ingest scan). `displaySku`/`displayDescription`/`upc` are text; the two flags
// boolean. `upc` is stored on the product's schema-extension table, not on
// `im_products_v3` itself — the catalog query joins it when provisioned.
// The internal `key` PK is intentionally excluded — it isn't shown in the
// catalog, so there's nothing for a user to filter on.
export const CATALOG_BASE_FILTER_COLUMNS = [
  "displaySku",
  "displayDescription",
  "upc",
  "isSellable",
  "isActive",
] as const;
export type CatalogBaseFilterColumn =
  (typeof CATALOG_BASE_FILTER_COLUMNS)[number];

export const CATALOG_BASE_BOOLEAN_COLUMNS: ReadonlySet<string> = new Set([
  "isSellable",
  "isActive",
]);

/**
 * True when a filter is a catalog-owned column (SKU, Description, UPC, flags)
 * rather than a raw ingest column. UPC is stored on the schema-extension table
 * but still skips the ingest scan. Every other column name is pushed down to
 * the ingest tables — the distinction both ends of the API branch on (the
 * server picks the query path; the client knows whether the page is
 * cursor-addressed and whether an exact total exists).
 */
export function isCatalogBaseFilterColumn(
  column: string,
): column is CatalogBaseFilterColumn {
  return (CATALOG_BASE_FILTER_COLUMNS as readonly string[]).includes(column);
}

/**
 * True when a filter set needs the ingest scan — i.e. at least one filter
 * targets a raw ingest column.
 */
export function catalogFiltersNeedIngestScan(
  filters: readonly { column: string }[] | undefined,
): boolean {
  return (filters ?? []).some(
    (filter) => !isCatalogBaseFilterColumn(filter.column),
  );
}

/**
 * An advanced filter restricted to the base ProductV3 columns — for the
 * endpoints that can only evaluate those (the exact count; see
 * CountItemCatalogInputSchema).
 */
export const CatalogBaseAdvancedFilterSchema =
  CatalogAdvancedFilterSchema.extend({
    column: z.enum(CATALOG_BASE_FILTER_COLUMNS),
  });
export type CatalogBaseAdvancedFilter = z.infer<
  typeof CatalogBaseAdvancedFilterSchema
>;

/** The base-column subset of a filter set (see CountItemCatalogInputSchema). */
export function catalogBaseFilters(
  filters: readonly CatalogAdvancedFilter[] | undefined,
): CatalogBaseAdvancedFilter[] {
  return (filters ?? []).filter((filter): filter is CatalogBaseAdvancedFilter =>
    isCatalogBaseFilterColumn(filter.column),
  );
}

/**
 * The raw-metadata-column subset of a filter set — the half evaluated against
 * the ingest rows, and so the half that decides which of a product's data links
 * matched (see ListProductRawDataInputSchema).
 */
export function catalogMetadataFilters(
  filters: readonly CatalogAdvancedFilter[] | undefined,
): CatalogAdvancedFilter[] {
  return (filters ?? []).filter(
    (filter) => !isCatalogBaseFilterColumn(filter.column),
  );
}

// ─── Record-key scope: source and location ───────────────────────────────────
// The catalog's two key filters, which narrow by *which record* a row is rather
// than by any column of it: `dataSourceIds` is the division a record's link
// belongs to — the "Source" column of the expanded raw tables — and `locations`
// is the record's own AS/400 location.
//
// A division IS answerable from the app DB: `im_product_v3_data_links` records
// which division's tables each product is linked through, so a Source-only
// scope narrows the plain product query (a semi-join on those links) and keeps
// its numbered pages and exact total. A location is not — it lives only in the
// ingest row — so a request carrying one is read by the ingest scan exactly as
// a filter on a raw column is (see {@link catalogNeedsIngestScan}):
// cursor-paged, with no exact total.

/**
 * One location value: a record's location code (`H`), or its whole CDB key
 * (`WJH`) — both select the same records, so a user can filter by the letter
 * they mean or paste the key they are reading (see `buildLocationCondition`).
 * Capped at the longest key any schema carries (company + division + location).
 */
export const CatalogLocationSchema = z.string().trim().min(1).max(12);

const catalogRecordKeyFields = {
  dataSourceIds: z.array(z.string().uuid()).max(50).optional(),
  locations: z.array(CatalogLocationSchema).max(50).optional(),
};

export const CatalogRecordKeyScopeSchema = z.object(catalogRecordKeyFields);
export type CatalogRecordKeyScope = z.infer<typeof CatalogRecordKeyScopeSchema>;

/** Whether either key filter is narrowing anything. */
export function catalogRecordKeyScopeActive(
  scope: CatalogRecordKeyScope,
): boolean {
  return (
    (scope.dataSourceIds?.length ?? 0) > 0 || (scope.locations?.length ?? 0) > 0
  );
}

/**
 * Whether a catalog request has to be read by the ingest scan rather than by the
 * plain product query — the single rule both ends of the API branch on: the
 * server picks the query path with it, the client learns from it that the page
 * is cursor-addressed and that no exact total exists.
 *
 * A raw-column filter or a Location needs the scan; a Source alone does not —
 * the product query narrows by division through the data links (see the
 * record-key scope note above). Walking every ingest table of the chosen
 * divisions for a term, as the scan does, costs a sequential scan per table
 * that has no trigram index on its item-number column: 10–30 s on Rexel
 * staging for one SKU search across three divisions.
 */
export function catalogNeedsIngestScan(
  request: CatalogRecordKeyScope & {
    advancedFilters?: readonly { column: string }[];
  },
): boolean {
  return (
    catalogFiltersNeedIngestScan(request.advancedFilters) ||
    (request.locations?.length ?? 0) > 0
  );
}

/**
 * Rejects a syntactically invalid regex before it reaches Postgres, so a typo'd
 * pattern surfaces as a clean 400 (path `search`) instead of a leaked DB error.
 * Uses JS `RegExp` as the validity gate — it catches the common syntax mistakes
 * (unbalanced parens/brackets, dangling quantifiers); it does not guarantee full
 * POSIX-ERE dialect parity with Postgres's `~*`.
 */
export function refineCatalogSearch(
  val: { search?: string; searchMode: ItemCatalogSearchMode },
  ctx: z.RefinementCtx,
): void {
  if (val.searchMode === "regex" && val.search) {
    try {
      new RegExp(val.search);
    } catch {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["search"],
        message: "Invalid regular expression.",
      });
    }
  }
}

/**
 * Restrict the catalog page to an explicit set of products (ProductV3.key) —
 * the keys a `resolveItemNumbers` call handed back. Composes with the
 * base-column filters and the change-set scope, but NOT with advanced filters
 * on raw ingest columns: that path supplies its own keys from the ingest scan,
 * so a request combining the two is rejected with a 400. `listCatalog` only —
 * clients derive the count of a keyed view from the resolver's matches, so
 * `countCatalog` doesn't take keys.
 */
const catalogKeysField = z.array(z.string().min(1)).min(1).max(10000);

// Paging + filter fields shared by the catalog page endpoints (the Gently list
// and the AS/400 view). `changeSetId` restricts the catalog to the products
// staged in one metadata change set — the review detail page's Queries views.
const catalogPageFields = {
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  advancedFilters: z.array(CatalogAdvancedFilterSchema).max(20).optional(),
  ...catalogFilterMatchField,
  changeSetId: z.string().uuid().optional(),
  // The Source and Location filters — see catalogRecordKeyFields. Omitted (or
  // empty) = every division and every location, each row naming its own.
  ...catalogRecordKeyFields,
  /**
   * Where to resume when the request needs the ingest scan — i.e. whenever an
   * advanced filter targets a raw ingest column or a Location is set, and
   * always for the AS/400 view. Such a page can't be addressed by number: the filters are evaluated
   * on the ingest server, one table at a time in item-number order, so "page 7"
   * could only be reached by redoing pages 1–6. `page` is ignored on that path;
   * clients follow `pageInfo.nextCursor` instead (and keep the cursors they
   * have visited to step back). Opaque — treat it as a token, not a position.
   */
  cursor: z.string().max(2048).optional(),
};

export const ListItemCatalogInputSchema = ItemCatalogFilterSchema.extend({
  ...catalogPageFields,
  keys: catalogKeysField.optional(),
  // Restrict the catalog to the products of one org-shared item list —
  // the same membership-subquery scope as `changeSetId`. A soft-deleted
  // list matches nothing.
  itemListId: z.string().uuid().optional(),
}).superRefine(refineCatalogSearch);

// ─── AS/400 view column groups ───────────────────────────────────────────────

export const As400ViewGroupSchema = z.enum([
  "ITEMMAST5",
  "ADDITIONAL_DESCRIPTION",
  "GAINS_COST",
  "ASSOCIATED_ITEMS",
  "QUANTITIES",
]);
export type As400ViewGroup = z.infer<typeof As400ViewGroupSchema>;

// ─── AS/400 export file formats ──────────────────────────────────────────────
// A second, mutually-exclusive set of column filters keyed to the AS/400
// item-master *export* file formats (ITEM, PURCHASE DATA, PRICE, ITEM ADD, STOCK
// DATA, STOCK ADD DATA, WEB ITEM KEYWORD, MATRIX CLASSIFICATION). Selecting one export format shows only
// that format's columns and clears any active "AS/400 View" groups (and
// vice-versa).
//
// Declaration order is the workbook's own tab order, which is also the order a
// published batch writes its files in (`PUBLISH_GROUP_ORDER`).
export const As400ExportGroupSchema = z.enum([
  "ITEM",
  "PURCHASE_DATA",
  "PRICE",
  "ITEM_ADD",
  "STOCK",
  "STOCK_ADD",
  "ITEM_KW",
  "MATRIX_CLASSIFICATION",
]);
export type As400ExportGroup = z.infer<typeof As400ExportGroupSchema>;

/**
 * The review page's worksheets, in two sections: the item master files a
 * product is set up through, then the stock and web keyword files that hang
 * off an item already there. The order the page lists them in — the
 * selector, Add row's worksheet list, the error summary, Review mode — not the
 * order the contract declares them in or a publish writes them in.
 */
export const AS400_EXPORT_GROUP_SECTIONS: readonly (readonly As400ExportGroup[])[] =
  [
    ["ITEM", "PURCHASE_DATA", "PRICE", "MATRIX_CLASSIFICATION", "ITEM_ADD"],
    ["STOCK", "STOCK_ADD", "ITEM_KW"],
  ];

/** Every worksheet, section by section. */
export const AS400_EXPORT_GROUP_ORDER: As400ExportGroup[] =
  AS400_EXPORT_GROUP_SECTIONS.flat();

/**
 * Each worksheet's English name, for files the server writes (the Review-mode
 * export). The page shows its own translated labels (the frontend's
 * `AS400_EXPORT_GROUP_LABELS`), which these match.
 */
export const AS400_EXPORT_GROUP_TITLES: Record<As400ExportGroup, string> = {
  ITEM: "Item",
  PURCHASE_DATA: "Purchase Data",
  PRICE: "Price",
  ITEM_ADD: "Item Add",
  STOCK: "Stock",
  STOCK_ADD: "Stock Add",
  ITEM_KW: "Web Item Keyword",
  MATRIX_CLASSIFICATION: "Matrix Classification",
};

/**
 * Export worksheets whose AS/400 file type has **no ingest mirror** — the
 * worksheet is declared, but there is no `<div>_as_…` table behind it to read a
 * single row from.
 *
 * `ITEM_KW` is here because Rexel's ingest feed carries no keyword file: the
 * `import` schema holds `_as_item`, `_as_itemad`, `_as_price`, `_as_prmxcl`,
 * `_as_purmasth` and `_as_purmastd` for each of the 15 division prefixes, and
 * nothing for keywords. The worksheet is declared ahead of the data so the
 * column layout, the file naming and the publish wiring are in place and
 * reviewable the day the table lands.
 *
 * What that means at runtime is deliberate rather than accidental, and every
 * path already reads this way — `resolveSources` drops a suffix that resolved
 * no readable table, leaving the group with zero sources:
 *
 * - the worksheet view counts 0 products in every division, so the review page
 *   greys them all out and says "This review has nothing under this worksheet";
 * - a download is refused with `NO_AS400_EXPORT_ROWS` rather than streaming an
 *   empty file;
 * - publish planning refuses the group outright, so it reaches no batch. The
 *   empty state standing in front of the editable grid is *not* what guarantees
 *   that: the grid is a client, and row authoring is refused server-side by
 *   {@link as400WorksheetHasIngestTable}.
 *
 * Consequences of the gap, spelled out rather than guessed at:
 *
 * - The four field codes below are **proposals**, not matches — there is no
 *   field listing and no mirror to check them against, hence `lowConfidence` on
 *   all four.
 * - `as_itemkw` is deliberately absent from {@link AS400_CDB_COLUMNS_BY_SCHEMA},
 *   {@link AS400_SKU_COLUMN_BY_SCHEMA} and
 *   {@link AS400_LOCATION_SPACE_BY_SCHEMA}. Those name a real table's *physical*
 *   columns, and there is no table: a schema with no entry has no CDB rather
 *   than guessing at column names.
 *
 * Delete the entry — and add the three schema mappings — when the keyword table
 * appears on the ingest mirrors.
 */
export const AS400_EXPORT_GROUPS_WITHOUT_INGEST_TABLE: ReadonlySet<As400ExportGroup> =
  new Set<As400ExportGroup>(["ITEM_KW"]);

/**
 * Whether this worksheet's file type is actually on the ingest mirrors.
 *
 * False means the worksheet is declared ahead of its mirror: it has columns and
 * a filename, but no rows to read and no record to change. Row authoring and
 * publish planning both refuse such a group, because a row authored against no
 * mirror has no branch to file it under and so could only ever become a batch
 * that names nothing.
 */
export function as400WorksheetHasIngestTable(group: As400ExportGroup): boolean {
  return !AS400_EXPORT_GROUPS_WITHOUT_INGEST_TABLE.has(group);
}

/**
 * Column sets for the AS/400 ITEMMAST5-style raw data. When the on-screen
 * "AS/400 View" toggle selects one or more groups, only the union of these
 * columns is kept from a linked raw-data table — in both the expanded row
 * and the CSV export, so the two stay in sync. Shared here so backend and
 * frontend don't duplicate the (large) column lists.
 */
export const AS400_VIEW_GROUP_COLUMNS: Record<
  As400ViewGroup,
  ReadonlySet<string>
> = {
  ITEMMAST5: new Set([
    "CDL",
    "ITEMNO",
    "PGSG",
    "PCSC",
    "IEDESC",
    "IFDESC",
    "PCPRC1",
    "ISLUOM",
    "IPPRQT",
    "PCPRC2",
    "PCPRC3",
    "PCPRC4",
    "PCPRC5",
    "PCPRC6",
    "PCPRC7",
    "PCPRC8",
    "PCPRC9",
    "IBCST",
    "ILCST",
    "ISUOM",
    "IALPH1",
    "IALPH2",
    "IALPH3",
    "ITARIF",
    "ISHPQT",
    "IQTCTN",
    "HAZMAT",
    "ISMQT1",
    "ISMAM1",
    "IBUYER",
    "ICODE1",
    "ICODE3",
    "ISUBCD",
    "ISUBNO",
    "ISPRD1",
    "ISUOM1",
    "IPAK1",
    "IGLO1",
    "UPC",
    "IPAK2",
    "IGLO2",
    "UPC2",
    "IPAK3",
    "IGLO3",
    "UPC3",
    "ISQTE1",
    "ISTAT",
    "ICODE4",
    "ICAT1",
    "ICAT2",
    "ICRTDT",
    "IRVDAT",
    "ISTKCD",
    "ISUPL1",
    "SUPP_NAME_CITY",
    "ISCTR1",
    "ISUFC1",
    "ISUPL2",
    "ISPRD2",
    "ISQTE2",
    "IMID2",
    "IITM2",
    "QTYSOLD01",
    "QOHSTK01",
    "AVAILABLE",
    "SQTYOO01",
    "ASSOCITEM",
    "ICAT5",
    "IOWRN1",
    "IOWRN2",
    "IOWRN3",
    "ICODE2",
    "ISNAM2",
    "IMXCLASS",
    "IOTH1E",
    "IOTH2E",
    "IOTH1F",
    "IOTH2F",
    "ISTATH",
    "IMXPRLN",
    "ISUPL3",
    "ISPRD3",
    "ISCTR3",
    "ISNAM3",
    "IMXPRCL",
    "IMXPRTG",
    "IMXPRFL",
    "ICCST",
    "ICODE9",
    "ICODE6",
    "ICODE5",
    "ICODE7",
    "ICODE8",
    "ICAT3",
    "ICAT4",
    "ISQTE3",
    "IMXSCOR",
    "IWIGHT",
    "IWUOM",
    "IMXUPDF",
    "IMXRNDF",
    "IQTEU1",
    "IQTEU2",
    "IQTEU3",
    "IQTFU1",
    "IQTFU2",
    "IQTFU3",
    "IHIGEA",
    "IWIDEA",
    "ILENEA",
    "IWHTEA",
    "IDNSEA",
    "IHIGPG",
    "IWIDPG",
    "ILENPG",
    "IWHTPG",
    "IDNSPG",
    "IHIGPT",
    "IWIDPT",
    "ILENPT",
    "IWHTPT",
    "IDNSPT",
    "SUPPLIER_NAME",
    "ISUOM3",
    "ISMQT3",
    "ISMAM3",
    "ISUFC3",
    "IMXMCCHG",
  ]),
  ADDITIONAL_DESCRIPTION: new Set([
    "ITEMNO",
    "IEDESC",
    "ISTAT",
    "ISUPL1",
    "SUNAME",
    "IDLANG",
    "IDLINE",
    "IDDESC",
  ]),
  GAINS_COST: new Set([
    "CNTR#",
    "LINE#",
    "CNTDESC",
    "STRDATE",
    "ENDDATE",
    "VENDR#",
    "VNDCNTR",
    "LOC",
    "DSTRC",
    "PRODUCT",
    "MINQTY",
    "INCQTY",
    "BPQTY1",
    "BPCST1",
    "IBCST",
    "BPQTY2",
    "BPCST2",
    "BPQTY3",
    "BPCST3",
    "DETGLA",
    "DETCSF",
    "STATUS",
  ]),
  ASSOCIATED_ITEMS: new Set([
    "ASDIV",
    "ASLOC",
    "IGROUP",
    "ASPROD",
    "IEDESC",
    "ASITEM",
    "IEDESC01",
    "QTY",
    "SOLD FLAG",
    "TYPE",
  ]),
  QUANTITIES: new Set([
    "ITEM NO",
    "DIVISION",
    "BRANCH LETTER",
    "TOTAL ON HAND",
    "ALLOCATED",
    "PRE-ALLOCATED",
    "VDR",
    "BACK ORDER",
    "AVAILABLE",
    "BOOKED",
    "ON ORDER",
    "NET AVAILABLE",
    "INTER-DEPOT",
    "IN CONSIGNMENT",
    "LOC. IN WAREHOUSE",
    "BUCKET QTY",
    "UNIT COST",
  ]),
};

// ─── AS/400 record keys: company / division / branch ─────────────────────────

/**
 * The AS/400 key columns of one ingest schema, by role. Every file spells them
 * with its own prefix (`icomp`/`imxcmp`/`ascmp`/`acomp`…) and a few carry only
 * some of the three, so the roles are declared rather than positional: AS/400
 * names the branch `loc`(ation), and `as_gscost` carries that alone while
 * `as_pcovdet` carries no branch at all.
 */
export interface As400CdbColumns {
  company?: string;
  division?: string;
  /** The location column — the "B" users read as branch. */
  branch?: string;
}

/**
 * The company/division/branch ("CDB") key columns of each AS/400 ingest schema —
 * the keys that say which record a row actually is.
 *
 * Keys are schema definition names; the physical ingest tables carry a division
 * prefix (`qe_as_item`, `qo_as_item` → `as_item`). The mapping covers every
 * ingest schema that has any of the three, and is exhaustive by design: a
 * schema with no entry here has no CDB rather than guessing at column names.
 *
 * That is why `as_itemkw` is absent even though the Web Item Keyword worksheet
 * is declared: no such table is mirrored, so nobody has seen its key columns.
 * See {@link AS400_EXPORT_GROUPS_WITHOUT_INGEST_TABLE}.
 */
export const AS400_CDB_COLUMNS_BY_SCHEMA: Record<string, As400CdbColumns> = {
  as_gscost: { branch: "loc" },
  as_invms: { company: "icomp", division: "idiv", branch: "iloc" },
  as_item: { company: "icomp", division: "idiv", branch: "iloc" },
  as_itemad: { company: "idcmpx", division: "iddivx", branch: "idloc" },
  as_miitm: { company: "acomp", division: "adiv", branch: "aloc" },
  as_pcovdet: { company: "pcodcmp", division: "pcoddiv" },
  as_prass: { company: "ascmp", division: "asdiv", branch: "asloc" },
  as_price: { company: "pcomp", division: "pdiv", branch: "ploc" },
  as_prmxcl: { company: "imxcmp", division: "imxdiv", branch: "imxloc" },
  as_prsls: { company: "scomp", division: "sdiv", branch: "sloc" },
  as_purmastd: { company: "icomp", division: "idiv", branch: "iloc" },
  as_purmasth: { company: "icomp", division: "idiv", branch: "iloc" },
  as_stock: { company: "scomp", division: "sdiv", branch: "sloc" },
};

/**
 * The item-number (SKU) column of each AS/400 ingest schema — `itemno` on
 * `as_item` / `as_price` / `as_stock`, and each other file's own spelling
 * (`aitem`, `iditem`, `imxitem`, …).
 *
 * Shared with the catalog scan (which orders pages by it) and the raw-data
 * table (which freezes it as the leftmost column, ahead of the CDB key). A
 * schema absent from the map has no item number to pin or scan by.
 */
export const AS400_SKU_COLUMN_BY_SCHEMA: Record<string, string> = {
  as_gscost: "product",
  as_item: "itemno",
  as_itemad: "iditem",
  as_miitm: "aitem",
  as_pcovdet: "pcodprod",
  as_prass: "asprod",
  as_price: "itemno",
  as_prmxcl: "imxitem",
  as_prsls: "itemno",
  as_purmastd: "itemno",
  as_purmasth: "itemno",
  as_stock: "itemno",
};

/** Schema definition ITEMMAST5 narrows to PRICE RECORD ID C. */
export const AS_PRICE_SCHEMA_NAME = "as_price";

/** The as_price PRICE RECORD ID that the ITEMMAST5 view keeps. */
export const AS_PRICE_PRECID_C = "C";

export interface As400RecordFilter {
  column: string;
  equals: string;
}

export const AS400_RECORD_FILTER_BY_SCHEMA: Record<string, As400RecordFilter> =
  {
    [AS_PRICE_SCHEMA_NAME]: { column: "precid", equals: AS_PRICE_PRECID_C },
  };

/**
 * One worksheet column that the AS/400 stores across several ingest fields.
 * `parts` are the fields in position order, each with the width the AS/400
 * gives it; the worksheet column's value is their concatenation.
 */
export interface As400ComposedColumn {
  /** The worksheet column's edit key (its ingest field code). */
  column: string;
  parts: readonly { column: string; width: number }[];
}

/**
 * Worksheet columns whose upload-file cell spans more than one field of the
 * mirrored record, by ingest schema (as {@link AS400_CDB_COLUMNS_BY_SCHEMA}).
 *
 * `REXEL PROD CLASS 9999999` is one cell of seven digits in the upload file,
 * which the AS/400 load program splits into `IBARCD` (levels 1–4, five
 * characters) and `FILL01` (level 5, two). The mirror holds the two fields,
 * so a row read from `IBARCD` alone shows a five-digit class with its last
 * level missing — and a five-digit class is what the AS/400 rejects ("Rexel
 * product class 11512 invalid"). The review reads the column composed, so
 * what it shows, compares an edit against and writes is the whole class.
 */
export const AS400_COMPOSED_COLUMNS_BY_SCHEMA: Record<
  string,
  readonly As400ComposedColumn[]
> = {
  as_item: [
    {
      column: "ibarcd",
      parts: [
        { column: "ibarcd", width: 5 },
        { column: "fill01", width: 2 },
      ],
    },
  ],
};

/**
 * The entry a schema-keyed map holds for the schema behind a physical ingest
 * table, matched on the prefix boundary so `qe_as_itemad` resolves to
 * `as_itemad` and never to `as_item`.
 */
function schemaEntryForTable<T>(
  bySchema: Record<string, T>,
  tableName: string,
): T | undefined {
  const lower = tableName.toLowerCase();
  for (const [schemaName, entry] of Object.entries(bySchema)) {
    if (lower === schemaName || lower.endsWith(`_${schemaName}`)) {
      return entry;
    }
  }
  return undefined;
}

export function as400RecordFilterForTable(
  tableName: string,
): As400RecordFilter | undefined {
  return schemaEntryForTable(AS400_RECORD_FILTER_BY_SCHEMA, tableName);
}

export function as400RecordFilterAllows(
  schemaOrTable: string,
  value: unknown,
): boolean {
  const filter = as400RecordFilterForTable(schemaOrTable);
  if (!filter) return true;
  if (typeof value === "string") {
    return value === filter.equals;
  }
  if (typeof value === "number") {
    return String(value) === filter.equals;
  }
  return false;
}

/** The composed columns of one physical table (`qe_as_item` → `as_item`'s). */
export function as400ComposedColumnsForTable(
  tableName: string,
): readonly As400ComposedColumn[] {
  return schemaEntryForTable(AS400_COMPOSED_COLUMNS_BY_SCHEMA, tableName) ?? [];
}

/**
 * A composed column's value from its parts' values, in order. Each part but
 * the last is padded to its width when a later part carries a value, so a
 * short (trimmed) leading field keeps the later ones in their positions;
 * trailing blank parts add nothing. All parts blank: null.
 */
export function composeAs400ColumnValue(
  composed: As400ComposedColumn,
  partValues: readonly (string | null)[],
): string | null {
  const lastFilled = partValues.reduce(
    (last, value, index) => (value && value.trim() !== "" ? index : last),
    -1,
  );
  if (lastFilled === -1) return null;
  return composed.parts
    .slice(0, lastFilled + 1)
    .map(({ width }, index) => {
      const value = partValues[index] ?? "";
      return index < lastFilled ? value.padEnd(width) : value;
    })
    .join("");
}

/** A schema definition's item-number column, read case-insensitively. */
export function as400SkuColumnForSchema(
  schemaName: string,
): string | undefined {
  return AS400_SKU_COLUMN_BY_SCHEMA[schemaName.toLowerCase()];
}

/**
 * Which code space a schema's branch (location) column draws its letters from.
 *
 * "The same branch" is only a meaningful comparison inside one space:
 *
 * - `record` — where the record itself lives, the letter a CDB key ends in.
 *   `as_item`, `as_price`, `as_itemad`, … all share it (RD: A,B,C,F,H,I,J).
 * - `stocking` — where a division stocks, a different set of letters entirely.
 *   For RD the two are disjoint (`as_stock.sloc` is D,E,K,M,O,V); for WJ and
 *   QE they overlap by accident (both contain H / R), so equating them would
 *   sometimes match nothing and sometimes match the wrong branch — the
 *   GEN-2350 trap, measured on the live mirrors. RD pairs are listed in
 *   `rd-branch-correspondence.ts` (Vera 2026-09-16 / GEN-2525); compare
 *   through that table, never by letter equality.
 *
 * A schema with no entry has no comparable branch at all: `as_pcovdet` carries
 * none, and `as_gscost`'s letter-only key empirically spans both spaces
 * (RD: C,E,F,H,K,M,O,V).
 */
export type As400LocationSpace = "record" | "stocking";

export const AS400_LOCATION_SPACE_BY_SCHEMA: Record<
  string,
  As400LocationSpace
> = {
  as_invms: "record",
  as_item: "record",
  as_itemad: "record",
  as_prass: "record",
  as_price: "record",
  as_prmxcl: "record",
  as_purmastd: "record",
  as_purmasth: "record",
  as_miitm: "stocking",
  as_prsls: "stocking",
  as_stock: "stocking",
};

/** A schema definition's branch code space, or undefined when its branch (if
 * any) is not comparable to any other schema's. */
export function as400LocationSpaceForSchema(
  schemaName: string,
): As400LocationSpace | undefined {
  return AS400_LOCATION_SPACE_BY_SCHEMA[schemaName.toLowerCase()];
}

/**
 * The record-space branch a row of `tableName` is filed under — the branch an
 * item file is named for — given the row's division code (`RD`) and its own
 * branch key.
 *
 * A record-space table's branch is that already. A stocking-space table's
 * (`as_stock.sloc`) is another alphabet, and the publish batches and the
 * per-branch download are split by the item file's: RD stocks at K for the DC
 * whose item records live under A. It is translated where the correspondence
 * is known — RD's DC cross-reference ({@link itemMasterLetterForRdStock}) — and
 * is null otherwise. Letter equality is the GEN-2350 trap, so a stock location
 * with no known item-master partner has no branch to file under rather than a
 * guessed one.
 */
export function as400RecordBranchForTable(
  tableName: string,
  divisionCode: string,
  branch: string | null,
): string | null {
  if (branch === null) return null;
  const space = schemaEntryForTable(AS400_LOCATION_SPACE_BY_SCHEMA, tableName);
  if (space !== "stocking") return branch;
  if (as400DivisionCode(divisionCode) !== "RD") return null;
  return itemMasterLetterForRdStock(branch) ?? null;
}

/**
 * The other way: the stocking location (`as_stock.sloc`) that stocks a
 * division's item-master branch, where the correspondence is known (RD's DC
 * cross-reference), else null. What a stock row added at an RD location
 * starts with in its LOC cell.
 */
export function as400StockingBranchFor(
  divisionCode: string,
  recordBranch: string,
): string | null {
  if (as400DivisionCode(divisionCode) !== "RD") return null;
  return stockLetterForRdItemMaster(recordBranch) ?? null;
}

/** A schema's key columns by role, read case-insensitively. */
function cdbColumnsOfSchema(schemaName: string): As400CdbColumns | undefined {
  return AS400_CDB_COLUMNS_BY_SCHEMA[schemaName.toLowerCase()];
}

/** The schema behind a physical ingest table, matched on the prefix boundary so
 * `qe_as_itemad` resolves to `as_itemad` and never to `as_item`. */
function cdbColumnsOfTable(tableName: string): As400CdbColumns | undefined {
  return schemaEntryForTable(AS400_CDB_COLUMNS_BY_SCHEMA, tableName);
}

/** A schema's key columns in company/division/branch order (roles it has). */
function orderedCdbColumns(columns: As400CdbColumns): string[] {
  return [columns.company, columns.division, columns.branch].filter(
    (column): column is string => column !== undefined,
  );
}

/**
 * A schema definition's CDB key columns, in company/division/branch order.
 * Undefined for a schema the mapping doesn't cover — which is not the same as a
 * mapped schema whose table is missing the columns.
 */
export function as400CdbColumnsForSchema(
  schemaName: string,
): readonly string[] | undefined {
  const columns = cdbColumnsOfSchema(schemaName);
  return columns && orderedCdbColumns(columns);
}

/**
 * The CDB key columns of the schema behind a physical ingest table —
 * `qe_as_item` → `as_item`'s `icomp`/`idiv`/`iloc`. Empty for a table of an
 * unmapped schema.
 */
export function as400CdbColumnsForTable(tableName: string): readonly string[] {
  const columns = cdbColumnsOfTable(tableName);
  return columns ? orderedCdbColumns(columns) : [];
}

/**
 * The branch (location) column of the schema behind a physical ingest table —
 * `qe_as_price` → `ploc`. Undefined for a schema with no branch key of its own
 * (`as_pcovdet`) or one the mapping doesn't cover.
 */
export function as400BranchColumnForTable(
  tableName: string,
): string | undefined {
  return cdbColumnsOfTable(tableName)?.branch;
}

/**
 * The branch (location) column of one schema definition — `as_price` → `ploc`.
 * The same key as {@link as400BranchColumnForTable}, read from the shared schema
 * name every division's physical table conforms to rather than from the table.
 */
export function as400BranchColumnForSchema(
  schemaName: string,
): string | undefined {
  return cdbColumnsOfSchema(schemaName)?.branch;
}

/**
 * One record's CDB key: its key values trimmed and concatenated in
 * company/division/branch order — `W`/`J`/`H` reads as `WJH`. A blank key
 * contributes nothing rather than leaving a gap, so all-blank is `""` (the
 * record has no key to show).
 *
 * The keys are AS/400 char and numeric fields. Anything else — a null, an
 * object an ingest quirk left on the row — has no reading as one letter of a
 * key, so it drops out rather than concatenating as `[object Object]`.
 */
export function as400CdbKey(parts: readonly unknown[]): string {
  let key = "";
  for (const part of parts) {
    if (typeof part === "string") key += part.trim();
    else if (typeof part === "number" || typeof part === "bigint") {
      key += String(part);
    }
  }
  return key;
}

/**
 * The synthetic cells an export worksheet row carries its record key under — a
 * `__`-prefixed key, so the grid treats them as read-only metadata and they stay
 * out of the worksheet's own (workbook-verbatim) columns and download.
 *
 * `AS400_CDB_COLUMN` is the whole key as one string, for the review grid's
 * leading column; `AS400_BRANCH_COLUMN` is the branch alone, which is what the
 * export splits a branch-per-file division's rows by.
 */
export const AS400_CDB_COLUMN = "__cdb__";
export const AS400_BRANCH_COLUMN = "__branch__";

/** What a worksheet row is ordered by: which item, then which record of it. */
export interface As400RowOrderKey {
  itemNumber: string | null;
  cdb: string | null;
}

/** Code-unit order with blanks last — the same in Node and every browser. */
function compareBlankLast(a: string | null, b: string | null): number {
  if (!a || !b) return (a ? 0 : 1) - (b ? 0 : 1);
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * The canonical order of a review's worksheet rows: by item number, then by
 * CDB key (company, division, branch) — the order publishing writes a
 * division's branch files in. Every worksheet (Item, Purchase, Price, …)
 * orders its rows by this one key, so an item's rows line up across tabs:
 * the n-th row of EV91 is the same division's record on each.
 */
export function compareAs400RowOrder(
  a: As400RowOrderKey,
  b: As400RowOrderKey,
): number {
  return (
    compareBlankLast(a.itemNumber, b.itemNumber) ||
    compareBlankLast(a.cdb, b.cdb)
  );
}

/** A cell as the sort reads it: trimmed, with blank as no value. */
function sortText(value: string | null | undefined): string | null {
  const text = value?.trim();
  return text ? text : null;
}

const NUMERIC_CELL = /^[-+]?(\d+\.?\d*|\.\d+)$/;

/** Two non-blank cells ascending: numbers numerically and first, text by code unit. */
function compareFilled(x: string, y: string): number {
  const xNumeric = NUMERIC_CELL.test(x);
  const yNumeric = NUMERIC_CELL.test(y);
  if (xNumeric && yNumeric) return Math.sign(Number(x) - Number(y));
  if (xNumeric !== yNumeric) return xNumeric ? -1 : 1;
  return x < y ? -1 : x > y ? 1 : 0;
}

/** 1 for a blank, so blanks rank after every value. */
const blankRank = (value: string | null) => (value === null ? 1 : 0);

/**
 * Two cells in `direction` — the review's column sort (GEN-2714), the same on
 * the server, which sorts the review's rows, and in the grid, which sorts the
 * new rows it pins ahead of them: numbers numerically (and ahead of text), text by
 * code unit, and a blank always last — whichever way the sort runs.
 */
export function compareSortValues(
  a: string | null | undefined,
  b: string | null | undefined,
  direction: As400GridSortDirection,
): number {
  const x = sortText(a);
  const y = sortText(b);
  if (x === null || y === null) return blankRank(x) - blankRank(y);
  const order = compareFilled(x, y);
  return direction === "asc" ? order : -order;
}

/**
 * A division's AS/400 code, from its DataSource title: the company and division
 * letters of every CDB key its records carry (`WJ` of `WJH`), and the whole CDB
 * of a row with no ingest record behind it (an authored new row). Titles pick up
 * cosmetic prefixes and padding ("%RD", "rd "), so matching strips
 * non-alphanumerics and upper-cases.
 */
export function as400DivisionCode(title: string): string {
  return title.replace(/[^a-z0-9]/gi, "").toUpperCase();
}

/**
 * The branches of each AS/400 division, keyed by division code → branch codes.
 * Derived from the AS/400 ingest stock data: the location codes each division
 * actually stocks (single alphanumeric codes with at least 500 stock rows;
 * lower/uppercase variants and punctuation codes are EBCDIC-conversion noise of
 * the same locations). RM, RY, and WF carry no stock locations above noise, so
 * they have no entry and price division-wide only. Update a division's row here
 * if its branch set changes.
 *
 * These are **stocking locations** (`as_stock.sloc`), which is not the location
 * key the other file types are keyed by: RD stocks D/E/K/M/O/V while its item
 * records (`as_item.iloc`) are A/B/C/F/H/I/J and its prices (`as_price.ploc`)
 * are C/F/H, so the sets can be disjoint. Never use this list as item-master
 * locations (GEN-2350). The RD stock↔item-master pairs live in
 * `rd-branch-correspondence.ts` (GEN-2525). Never use this list to decide which
 * locations a worksheet's records exist under — read those from the records
 * (see `As400ExportViewService.changedBranchesByDataSource`). It is a pricing
 * input, nothing more.
 */
export const AS400_BRANCHES_BY_DIVISION: Record<string, readonly string[]> = {
  // prettier-ignore
  QE: ["2", "3", "5", "6", "8", "9", "D", "E", "F", "H", "J", "L", "M", "N", "O", "P", "R", "S", "T", "V", "W", "X", "Z"],
  // prettier-ignore
  QO: ["A", "B", "C", "D", "E", "G", "I", "J", "K", "L", "O", "Q", "R", "S", "V", "Y", "Z"],
  // prettier-ignore
  RA: ["8", "A", "B", "C", "G", "H", "J", "K", "L", "M", "N", "S", "T", "W"],
  RD: ["D", "E", "K", "M", "O", "V"],
  RH: ["E", "N", "S"],
  RL: ["B", "C", "E", "F", "I", "J"],
  UU: ["B", "O"],
  // prettier-ignore
  WB: ["A", "C", "D", "F", "G", "H", "I", "J", "L", "N", "P", "Q", "R"],
  // prettier-ignore
  WG: ["0", "1", "4", "6", "8", "A", "B", "D", "E", "F", "G", "H", "I", "J", "K", "M", "N", "O", "P", "Q", "R", "T", "U", "V", "W", "X", "Y", "Z"],
  // prettier-ignore
  WJ: ["2", "6", "7", "8", "A", "B", "D", "E", "F", "H", "J", "K", "L", "M", "N", "O", "P", "T", "V", "Y"],
  // prettier-ignore
  WR: ["1", "2", "C", "D", "E", "G", "H", "I", "J", "K", "M", "N", "O", "P", "W", "X", "Y"],
  // prettier-ignore
  WT: ["1", "3", "A", "C", "D", "E", "F", "G", "H", "I", "K", "M", "N", "S", "T", "U", "V", "W", "X", "Y", "Z"],
};

/**
 * The branch letters for a division, given its DataSource title (normalized to
 * its AS/400 code by {@link as400DivisionCode}). Empty for a division that
 * stocks no branches of its own.
 */
export function as400BranchesForDivision(title: string): readonly string[] {
  return AS400_BRANCHES_BY_DIVISION[as400DivisionCode(title)] ?? [];
}

/**
 * Divisions whose upload runs per branch (RD: one division-wide file cannot be
 * loaded), with the item-master locations (`as_item.iloc`) an authored row is
 * added at — one row per location picked (GEN-2720). RD's are the six DCs of
 * Vera-Lynne's cross-reference, confirmed 2026-09-29; the unpaired I gets no
 * new rows. Fixed, not read from the records: the set changes only when a DC
 * opens or closes, and reading it live was a `SELECT DISTINCT` over each
 * multi-million-row mirror per dialog open. Not the stocking list
 * ({@link AS400_BRANCHES_BY_DIVISION}), a disjoint code space for RD.
 */
export const AS400_BRANCH_SPLIT_LOCATIONS_BY_DIVISION: Record<
  string,
  readonly string[]
> = {
  RD: RD_DC_CROSS_REFERENCES.map((dc) => dc.itemMasterLetter).sort(),
};

/** The divisions above, as {@link as400DivisionCode} normalizes a title. */
export const AS400_BRANCH_SPLIT_DIVISIONS: ReadonlySet<string> = new Set(
  Object.keys(AS400_BRANCH_SPLIT_LOCATIONS_BY_DIVISION),
);

/** Whether this division's worksheet export is split one file per branch. */
export function as400DivisionExportsPerBranch(title: string): boolean {
  return AS400_BRANCH_SPLIT_DIVISIONS.has(as400DivisionCode(title));
}

/** The locations Add row offers a division; empty unless split by branch. */
export function as400BranchSplitLocationsForDivision(
  title: string,
): readonly string[] {
  return (
    AS400_BRANCH_SPLIT_LOCATIONS_BY_DIVISION[as400DivisionCode(title)] ?? []
  );
}

/**
 * Column layout for the AS/400 item-master *export* worksheets. Each export
 * group reproduces one tab of the Rexel "Product Upload" workbook: the
 * on-screen Export table and the CSV/XLSX download show that tab's columns
 * verbatim — same headings, same order, none dropped.
 *
 *   ITEM                  → "ITEM WORKSHEET"            (source: item.json)
 *   PURCHASE_DATA         → "PURCHASE DATA WORK SHEET"  (purmasth.json + purmastd.json)
 *   PRICE                 → "PRICE WORK SHEET"          (price.json)
 *   ITEM_ADD              → "ITEM ADD WORKSHEET"        (itemad.json)
 *   STOCK                 → "STOCK DATA WORK SHEET"     (stock.json)
 *   STOCK_ADD             → "STOCK ADD DATA WORKS SHEET" (stock.json — keys only)
 *   ITEM_KW              → "WEB ITEM KEYWORD"          (no ingest file — see
 *                           {@link AS400_EXPORT_GROUPS_WITHOUT_INGEST_TABLE})
 *   MATRIX_CLASSIFICATION → "MATRIX CLASSIFICATION"     (prmxcl.json)
 *
 * `header` is the worksheet heading, kept verbatim (may contain line breaks or
 * be empty). `code` is the AS/400 raw-table field the value is sourced from, or
 * `null` when the column has no counterpart we can fill yet — export-only
 * "change flag"/"reset"/"update Y/N" pseudo-columns and a few item fields with
 * no ingest field (HazMat, shelf-life, serialization); those render blank.
 * `lowConfidence` flags a probable-but-unconfirmed field match.
 *
 * `source` declares which of the group's ingest file types owns `code`, as the
 * table-name suffix from {@link AS400_EXPORT_GROUP_SCHEMA_SUFFIXES}. It is what
 * maps an edited export cell back to the ingested raw column it must update —
 * required on every mapped column of a multi-file-type group (Purchase Data),
 * where "the first table that happens to have a column of that name" is not a
 * safe rule. Single-file-type groups omit it; their owner is unambiguous.
 */
/**
 * A worksheet's number mask, as the AS/400 load program reads a numeric cell:
 * at most `integerDigits` before the point and `fractionDigits` after it. A
 * value may always be *shorter* than the mask (`0.1` and `1` both fit
 * `9999.999`) but never longer, since the load program has no room for the
 * extra digits. No point in the mask (`fractionDigits: 0`) means whole numbers
 * only.
 */
export interface As400NumberFormat {
  /** Digits allowed before the point; undefined when the field states none. */
  integerDigits?: number;
  /** Digits allowed after the point; 0 = a whole number. */
  fractionDigits: number;
  /**
   * Values the column documents as instructions rather than numbers, accepted
   * as typed: `CORE COST`'s "Enter -1 to clear the existing value".
   */
  clearValues?: readonly string[];
}

export interface As400ExportColumn {
  /** Worksheet heading, verbatim (may contain line breaks or be empty). */
  header: string;
  /** AS/400 raw-table field the value is sourced from; null = unmapped (blank). */
  code: string | null;
  /**
   * Stable identity for an export-only column (`code: null`): the worksheet's
   * change-flag / reset / update-Y/N pseudo-columns and the few item fields
   * with no ingest counterpart. Giving them a key is what makes them editable
   * in a review — their staged values are stored under it, deliberately apart
   * from the ingest field codes so a future ingest sync can never mistake a
   * worksheet-only value for an ingest field edit. A `code: null` column with
   * no key (the Item Add spacer) stays read-only.
   *
   * Keys are unique within their group, across codes and keys alike.
   */
  key?: string;
  /**
   * Owning ingest file type: the table-name suffix `code` lives under. Set on
   * every mapped column of a group with more than one file type.
   */
  source?: string;
  /**
   * Part of the record key the AS/400 finds the row by in a change-mode
   * upload (the worksheet-visible part — company/division/location travel in
   * the file's name, not its cells). A change file carries only the cells the
   * review actually changes, blank meaning "do not change"; identity columns
   * are the exception and always carry their value, or the row would name no
   * record at all. The product number everywhere, plus language and line
   * number on Item Add and Web Item Keyword, whose records are keyed per
   * description line and per keyword line respectively.
   */
  identity?: true;
  /** True when the field match is a best guess, not confirmed. */
  lowConfidence?: boolean;
  /**
   * The most characters the AS/400 accepts in this column's cell, when the
   * workbook states one. Enforced on the review grid so an upload file can't
   * be authored with a value the AS/400 will reject or silently truncate.
   *
   * A cell holds *text*, so this is the character width of the value as it is
   * typed — not the field's storage size. Sources, strongest first:
   *
   * - **The AS/400 field listing for the file.** Its `Length` is the field's
   *   byte footprint in the record, which is the character width only for type
   *   `A`. Packed (`P`) and binary (`B`) fields state their digits separately
   *   (`IBCST` is `Length 5` but 9 digits — packed decimal stores two digits a
   *   byte plus a sign nibble). Take `Length` for `A`, digit count for `P`/`B`.
   *   The whole ITEM group is encoded from its listing.
   * - The column's own workbook note — "Max length: 10A", "Character 5".
   * - The header's own mask — `SHP'G MULT 99999` (5), `LEAD TIME 999` (3).
   *   A mask is worksheet-level and can span more than one ingest field, and
   *   then the mask is the limit, not the field the column is read from:
   *   `REXEL PROD CLASS 9999999` is 7 digits in the upload file, which the
   *   load program stores as levels 1–4 in `IBARCD` (5) and level 5 in
   *   `FILL01` (2). Capped at 5 it refused the value the AS/400 wants — the
   *   AS/400 rejects a 5-digit class ("Rexel product class 11512 invalid").
   * - The ingest field's `NUMERIC(precision, scale)` in
   *   `backend/test-data/rexel/as400_schema_jsons`, whose `precision` is the
   *   same digit count the listings give (the fixtures derive from them).
   * - A Y/N (or Y/N/D) pseudo-column: one character.
   * - Carried across files on the owner's say-so, where no listing covers the
   *   file yet: the product number is 20 in all six groups (`ITEMNO`,
   *   `IDITEM`, `IKITEM`, `IMXITEM` — it is the same item-master key
   *   everywhere), and `IDDESC` is 40, matching the item file's descriptions.
   *
   * **A numeric column is its digit count plus one for the point.** That
   * includes the headers marked `2 IMPL DEC`, `5 IML DEC` or `2 IMP DEC`:
   * despite the wording, AS/400 users type the point there (`1234.56`), and
   * the header only states how many decimals the field keeps. Note also that a
   * leading `-` spends one of these characters, the masks having no sign
   * position — the documented "enter -1 to clear" idiom still fits.
   *
   * **Undefined means unknown, not unlimited.** Every AS/400 field has a real
   * width, but only the ITEM listing is on hand: the ingest fixtures carry no
   * length for alpha fields, and the ingest tables type every one of them
   * `VARCHAR(255)` regardless (see `SQL_SCHEMA_STRING_MAX_LENGTH`). ITEM,
   * PRICE, ITEM_KW (whose four cell notes each state one) and
   * MATRIX_CLASSIFICATION are complete; the 31 columns left undefined are 29 of
   * PURCHASE_DATA's (supplier codes, historical UPC split fields, names,
   * remarks; Supplier/Item UPC is 12, GEN-2768; the quote numbers' 20 is
   * Rexel's word, GEN-2771, and warn codes' 3 is GEN-2774) and
   * ITEM_ADD's `IDLANG`/`IDLINE`. They are waiting on the field listings for
   * purmasth, purmastd and itemad. STOCK and STOCK_ADD state limits only where
   * the workbook or `stock.json` does (the notes, the masks, the Y/N flags,
   * the product number and the one-character location); their other columns
   * wait on a stock listing. A guessed limit would silently corrupt an
   * upload file, so they stay unconstrained until then.
   */
  maxLength?: number;
  /**
   * Spaces in this column's value are part of it. The AS/400 reads the field
   * by position — a right-justified code, a flag "in 2nd position" — so a
   * leading space is data, and the grid keeps every space the author types
   * where it would otherwise trim the value on commit (and shows each one).
   * Unset means the field is ordinary text, where a stray space is a slip.
   */
  keepWhitespace?: true;
  /**
   * The numeric format this column's value must follow, for a column whose
   * header carries no mask to read it from, or whose mask needs more than the
   * header says. Most numeric columns state their mask in the header
   * (`WEIGHT 9999.999`), and {@link as400ColumnNumberFormats} reads it there.
   * This is the explicit form for the rest: the dimension columns (no mask;
   * `NUMERIC(9,4)` on the ingest mirror, so 5.4), the `5 IML DEC` / `2 IMP DEC`
   * columns (whose header names only the decimals), the integer MIN QTY
   * columns, and a column with a documented clear value (`CORE COST`'s `-1`).
   */
  numberFormat?: As400NumberFormat;
  /**
   * The value that blanks this field on the AS/400: a change-mode upload reads
   * an empty cell as "do not change", so clearing a field takes a run of `/`
   * whose length the column's own worksheet note states ("Enter '/////' for
   * blank value"). Set exactly where the note gives one — written out here so
   * the grid can offer it without parsing prose, and pinned to the note by a
   * contract test. Unset means the worksheet documents no way to blank the
   * field (reset flags, Y/N columns, and fields such as ICAT3 whose note says
   * otherwise).
   */
  blankMarker?: string;
  /** Worksheet cell note, shown as a header tooltip. Verbatim (may be multi-line). */
  comment?: string;
}

/**
 * The identity an export column's cells are read and edited by: its ingest
 * field code when mapped, its export-only key otherwise, null when neither
 * (a spacer — not editable, always blank).
 */
export function as400ColumnEditKey(column: As400ExportColumn): string | null {
  return column.code ?? column.key ?? null;
}

/**
 * The export-only column keys of a group — the editable columns whose values
 * live only on the worksheet, never in an ingest table.
 */
export function as400ExportOnlyColumnKeys(
  group: As400ExportGroup,
): ReadonlySet<string> {
  const keys = new Set<string>();
  for (const column of AS400_EXPORT_WORKSHEET_COLUMNS[group]) {
    if (!column.code && column.key) keys.add(column.key);
  }
  return keys;
}

/**
 * Ingest table-name suffixes identifying each export group's AS/400 file
 * type(s): a data-source link feeds a group when its `foreign_table_name` ends
 * in one of these (e.g. `qe_as_price` → `_as_price`). A group with more than
 * one suffix has its file types joined per division on the group's join key —
 * the first mapped worksheet column.
 *
 * Lives here, next to the column mapping, so "which raw table owns this
 * worksheet column" ({@link As400ExportColumn.source}) and "which raw tables
 * feed this worksheet" are declared in one place.
 */
export const AS400_EXPORT_GROUP_SCHEMA_SUFFIXES: Record<
  As400ExportGroup,
  readonly string[]
> = {
  ITEM: ["_as_item"],
  PURCHASE_DATA: ["_as_purmasth", "_as_purmastd"],
  PRICE: ["_as_price"],
  ITEM_ADD: ["_as_itemad"],
  // Both stock worksheets read the stock record: Stock its fields, Stock Add
  // only its location and product — the replenishment settings it carries are
  // on no ingest file, so the record is what a change row is keyed against.
  STOCK: ["_as_stock"],
  STOCK_ADD: ["_as_stock"],
  // Named by the pattern every other suffix follows — `_as_` plus the AS/400
  // file's own name — so it resolves the day the table is mirrored without
  // anything here changing. No `<div>_as_itemkw` table exists yet
  // ({@link AS400_EXPORT_GROUPS_WITHOUT_INGEST_TABLE}); the suffix match is
  // `endsWith`, and `qe_as_itemkw` does not end in `_as_item`, so declaring it
  // early cannot pull the item file's tables into this group.
  ITEM_KW: ["_as_itemkw"],
  MATRIX_CLASSIFICATION: ["_as_prmxcl"],
};

// Number formats shared by several columns, from their header notes and the
// ingest mirrors' NUMERIC(precision, scale): the dimensions are NUMERIC(9,4)
// with no header mask; `5 IML DEC` and `2 IMP DEC` / `2 IMPL DEC` are
// NUMERIC(9,5) and NUMERIC(9,2) — the point is typed, despite "IMPL" (see
// As400ExportColumn.maxLength); MIN QTY is an integer.
const DIMENSION_FORMAT: As400NumberFormat = {
  integerDigits: 5,
  fractionDigits: 4,
};
const UOM_FACTOR_FORMAT: As400NumberFormat = {
  integerDigits: 4,
  fractionDigits: 5,
};
const CENTS_FORMAT: As400NumberFormat = {
  integerDigits: 7,
  fractionDigits: 2,
};
const WHOLE_NUMBER_FORMAT: As400NumberFormat = { fractionDigits: 0 };
// The matrix price levels: "Numeric 11 (4 decimals)", NUMERIC(11,4).
const MATRIX_PRICE_FORMAT: As400NumberFormat = {
  integerDigits: 7,
  fractionDigits: 4,
};

// The two Purchase Data file types, named once for the column annotations.
const PURMASTH = "_as_purmasth";
const PURMASTD = "_as_purmastd";

export const AS400_EXPORT_WORKSHEET_COLUMNS: Record<
  As400ExportGroup,
  readonly As400ExportColumn[]
> = {
  ITEM: [
    {
      header: "PRODUCT NUMBER",
      code: "ITEMNO",
      identity: true,
      maxLength: 20,
      comment: "Must be all upper case and cannot contain space.",
    },
    { header: "PG", code: "IGROUP", maxLength: 2 },
    { header: "SG", code: "ISGRUP", maxLength: 2 },
    { header: "PC", code: "ICLASS", maxLength: 2 },
    { header: "SC", code: "ISCLAS", maxLength: 2 },
    { header: "ENGLISH DESCRIPTION", code: "IEDESC", maxLength: 40 },
    { header: "FRENCH DESCRIPTION", code: "IFDESC", maxLength: 40 },
    {
      header: "KEYWORD 1 (10 CHARS)",
      code: "IALPH1",
      maxLength: 10,
      comment: "Mandatory, cannot be blanked",
    },
    {
      header: "KEYWORD 2 (10 CHARS)",
      code: "IALPH2",
      maxLength: 10,
      blankMarker: "//////////",
      comment: "Max length: 10A\nEnter '////////// for blank value",
    },
    {
      header: "KEYWORD 3 (10 CHARS)",
      code: "IALPH3",
      maxLength: 10,
      blankMarker: "//////////",
      comment: "Max length: 10A\nEnter '////////// for blank value",
    },
    { header: "TARIFF", code: "ITARIF", maxLength: 2 },
    { header: "SHP'G MULT 99999", code: "ISHPQT", maxLength: 5 },
    { header: "CTN QTY 9999", code: "IQTCTN", maxLength: 4 },
    { header: "BUYER", code: "IBUYER", maxLength: 1 },
    { header: "WEIGHT 9999.999", code: "IWIGHT", maxLength: 8 },
    { header: "LB or KG", code: "IWUOM", maxLength: 2 },
    { header: "STOCK UOM", code: "ISUOM", maxLength: 2 },
    { header: "PRICING FACTOR 9999.99999", code: "IPPRQT", maxLength: 10 },
    { header: "SELL UOM", code: "ISLUOM", lowConfidence: true, maxLength: 2 },
    { header: "BUYER'S COST 99999.9999", code: "IBCST", maxLength: 10 },
    { header: " LANDED COST 99999.9999", code: "ILCST", maxLength: 10 },
    { header: "USER DEF 1", code: "ICODE1", maxLength: 1 },
    { header: "USER DEF 2", code: "ICODE2", maxLength: 1 },
    {
      header: "USER DEFINED FIELD 3 (10 CHARS)",
      code: "ICODE3",
      maxLength: 10,
      // Position-sensitive, like the item categories: the AS/400 reads the
      // field by character position, so `    N` (N in position 5) is not `N`.
      keepWhitespace: true,
      blankMarker: "/",
      comment: "Enter '/' to blank out field in an update mode",
    },
    { header: "LEAD TIME 999", code: "IPLEAD", maxLength: 3 },
    { header: "SUB ", code: "ISUBCD", maxLength: 1 },
    {
      header: "SUBSTITUTE PROD CODE (20 CHARS)",
      code: "ISUBNO",
      maxLength: 20,
      comment:
        "Allow Sub Item to be blanked out if Sub Code = '0' and uploaded Sub Item has all '/'s' in it",
    },
    { header: "PROD REC STATUS (1 CHAR)", code: "ISTAT", maxLength: 1 },
    {
      header: "UPDATE PROD SEQ # (Y/N)",
      code: null,
      key: "UPDATE_IPRDSQ",
      maxLength: 1,
    },
    { header: "PROD SEQ # 9999999", code: "IPRDSQ", maxLength: 7 },
    {
      header: "REXEL PROD CLASS 9999999",
      code: "IBARCD",
      lowConfidence: true,
      // The mask, not IBARCD's 5: the file column carries all 7 digits, and
      // the AS/400 splits them into IBARCD + FILL01 (see maxLength above).
      maxLength: 7,
    },
    {
      header: "CHANGE STATUS FLAG (Y/N)",
      code: null,
      key: "CHANGE_STATUS_FLAG",
      maxLength: 1,
    },
    { header: "Qty Per Case1 Eng", code: "IQTEU1", maxLength: 5 },
    { header: "Qty Per Case2 Eng", code: "IQTEU2", maxLength: 5 },
    { header: "Qty Per Case3 Eng", code: "IQTEU3", maxLength: 6 },
    { header: "Qty Per Case1 Frn", code: "IQTFU1", maxLength: 5 },
    { header: "Qty Per Case2 Frn", code: "IQTFU2", maxLength: 5 },
    { header: "Qty Per Case3 Frn", code: "IQTFU3", maxLength: 6 },
    {
      header: "USER DEF 4",
      code: "ICODE4",
      maxLength: 1,
      blankMarker: "/",
      comment:
        "User defined field 4.\nMax length: 1AN\nEnter '/' for blank value.\nValidation based on user defined constant 'DTMH'.",
    },
    {
      header: "USER DEF 5",
      code: "ICODE5",
      maxLength: 1,
      blankMarker: "/",
      comment:
        "User defined field 5.\nMax length: 1AN\nEnter '/' for blank value.\nValidation based on user defined constant 'DTMH'.",
    },
    {
      header: "USER DEF 6",
      code: "ICODE6",
      maxLength: 1,
      blankMarker: "/",
      comment:
        "User defined field 6.\nMax length: 1AN\nEnter '/' for blank value.\nValidation based on user defined constant 'DTMH'.",
    },
    {
      header: "USER DEF 7",
      code: "ICODE7",
      maxLength: 1,
      blankMarker: "/",
      comment:
        "User defined field 7.\nMax length: 1AN\nEnter '/' for blank value.\nValidation based on user defined constant 'DTMH'.",
    },
    {
      header: "USER DEF 8",
      code: "ICODE8",
      maxLength: 1,
      blankMarker: "/",
      comment:
        "User defined field 8.\nMax length: 1AN\nEnter '/' for blank value.\nValidation based on user defined constant 'DTMH'.",
    },
    {
      header: "USER DEF 9",
      code: "ICODE9",
      maxLength: 1,
      blankMarker: "/",
      comment:
        "User defined field 9.\nMax length: 1AN\nEnter '/' for blank value.\nValidation based on user defined constant 'DTMH'.",
    },
    {
      header: "Item Cateory1",
      code: "ICAT1",
      maxLength: 5,
      keepWhitespace: true,
      blankMarker: "/////",
      comment:
        "Item Category 1.\nMax length: 5AN\nRight justified\nEnter '/////' for blank value.",
    },
    {
      header: "Item Cateory2",
      code: "ICAT2",
      maxLength: 5,
      keepWhitespace: true,
      blankMarker: "/////",
      comment:
        "Item Category 2.\nMax length: 5AN\nRight justified\nEnter '/////' for blank value.",
    },
    {
      header: "Item Cateory3",
      code: "ICAT3",
      maxLength: 5,
      keepWhitespace: true,
      comment:
        "Item Category 3.\nMax length: 5AN\nRight justified\nEnter 'Y' or 'N' value in 2nd position.\nUsed to flag the product as an Assembly Item",
    },
    {
      header: "Item Cateory4",
      code: "ICAT4",
      maxLength: 5,
      keepWhitespace: true,
      blankMarker: "/////",
      comment:
        "Item Category 4.\nMax length: 5AN\nRight justified\nEnter '/////' for blank value.",
    },
    {
      header: "Item Cateory5",
      code: "ICAT5",
      maxLength: 5,
      keepWhitespace: true,
      blankMarker: "/////",
      comment:
        "Item Category 5.\nMax length: 5AN\nRight justified\nEnter '/////' for blank value.",
    },
    {
      header: "Change Flag for Qty Per Case1 Eng",
      code: null,
      key: "CHG_IQTEU1",
      maxLength: 1,
    },
    {
      header: "Change Flag for Qty Per Case2 Eng",
      code: null,
      key: "CHG_IQTEU2",
      maxLength: 1,
    },
    {
      header: "Change Flag for Qty Per Case3 Eng",
      code: null,
      key: "CHG_IQTEU3",
      maxLength: 1,
    },
    {
      header: "Change Flag for Qty Per Case1 Frn",
      code: null,
      key: "CHG_IQTFU1",
      maxLength: 1,
    },
    {
      header: "Change Flag for Qty Per Case2 Frn",
      code: null,
      key: "CHG_IQTFU2",
      maxLength: 1,
    },
    {
      header: "Change Flag for Qty Per Case3 Frn",
      code: null,
      key: "CHG_IQTFU3",
      maxLength: 1,
    },
    {
      header: "Prd Type",
      code: "ITYPE",
      maxLength: 1,
      comment:
        "Valid Product Type =\n'T' (POTF Template)\n'W'(AOTF Template)\n'F' and ' '\nMax Length 1A",
    },
    {
      header: "HazMat",
      code: null,
      key: "HAZMAT_FLAG",
      maxLength: 1,
      comment:
        "Hazardous Material ADD mode -must be Y or N. CHANGE mode must be Y or N or Blank (do not change)",
    },
    {
      header: "SHELF LIFE CONTROLLED FLAG",
      code: null,
      key: "SHELF_LIFE_FLAG",
      maxLength: 1,
      comment: "Shelf Life Controlled Flag\nMax Length: 1AN\nValid Values: Y/N",
    },
    {
      header: "CORE COST 99999.9999",
      code: "ICCST",
      maxLength: 10,
      // The header's own 99999.9999, plus the documented clear value.
      numberFormat: {
        integerDigits: 5,
        fractionDigits: 4,
        clearValues: ["-1"],
      },
      comment:
        "Note:\n-Leave this field blank or zero if no change is make to this field.\n-Enter -1 to clear the existing value on the item master file.",
    },
    {
      header: "Product Serialization Flag (Y/N)",
      code: null,
      key: "SERIALIZATION_FLAG",
      maxLength: 1,
    },
  ],
  // Purchase Data spans two file types: supplier data (purmasth) and dimension
  // data (purmastd). Every mapped column declares which one owns it — the join
  // key (ITEMNO) exists in both, and an edit to it is fanned out to both rows.
  PURCHASE_DATA: [
    {
      header: "\nPRODUCT CODE",
      code: "ITEMNO",
      source: PURMASTH,
      identity: true,
      maxLength: 20,
    },
    { header: "SUPL#\n1", code: "ISUPL1", source: PURMASTH },
    { header: "CNTRY", code: "ISCTR1", source: PURMASTH },
    {
      header: "SUPPLIER 1\nPRODUCT #",
      code: "ISPRD1",
      source: PURMASTH,
      maxLength: 25,
      blankMarker: "/////////////////////////",
      comment:
        ".Supplier 1 Prouduct #\n.Max length: 25 (AN)\n.Enter '/////////////////////////' (total 25 '/')             for blank value.\n.Leave this field blank if no uploaded data",
    },
    { header: "UOM", code: "ISUOM1", source: PURMASTH },
    {
      header: "UOM\nFACTOR\n5 IML DEC",
      code: "ISUFC1",
      source: PURMASTH,
      maxLength: 10,
      numberFormat: UOM_FACTOR_FORMAT,
    },
    {
      header: "MIN\nQTY",
      code: "ISMQT1",
      source: PURMASTH,
      numberFormat: WHOLE_NUMBER_FORMAT,
    },
    {
      header: "MIN\nAMT $\n2 IMP DEC",
      code: "ISMAM1",
      source: PURMASTH,
      maxLength: 10,
      numberFormat: CENTS_FORMAT,
    },
    { header: "UPC PACK 1ST Char", code: "IPAK1", source: PURMASTH },
    { header: "UPC MFTR ID 2ND Char", code: "IMID1", source: PURMASTH },
    {
      header: "SUPLR\nUPC",
      code: "IGLO1",
      source: PURMASTH,
      lowConfidence: true,
      maxLength: 12,
    },
    { header: "ITEM\nUPC", code: "IITM1", source: PURMASTH, maxLength: 12 },
    { header: "SD", code: "ISHD1", source: PURMASTH },
    { header: "SUPL#\n2", code: "ISUPL2", source: PURMASTH },
    { header: "CNTRY", code: "ISCTR2", source: PURMASTH },
    {
      header: "SUPPLIER 2\nPRODUCT #",
      code: "ISPRD2",
      source: PURMASTH,
      maxLength: 25,
      blankMarker: "/////////////////////////",
      comment:
        ".Supplier 2 Prouduct #\n.Max length: 25 (AN)\n.Enter '/////////////////////////' (total 25 '/')             for blank value.\n.Leave this field blank if no uploaded data.",
    },
    { header: "UOM", code: "ISUOM2", source: PURMASTH },
    {
      header: "UOM\nFACTOR\n5 IML DEC",
      code: "ISUFC2",
      source: PURMASTH,
      maxLength: 10,
      numberFormat: UOM_FACTOR_FORMAT,
    },
    {
      header: "MIN\nQTY",
      code: "ISMQT2",
      source: PURMASTH,
      numberFormat: WHOLE_NUMBER_FORMAT,
    },
    {
      header: "MIN\nAMT $\n2 IMPL DEC",
      code: "ISMAM2",
      source: PURMASTH,
      maxLength: 10,
      numberFormat: CENTS_FORMAT,
    },
    { header: "UPC PACK 1ST Char", code: "IPAK2", source: PURMASTH },
    { header: "UPC MFTR ID 2ND Char", code: "IMID2", source: PURMASTH },
    {
      header: "SUPLR\nUPC",
      code: "IGLO2",
      source: PURMASTH,
      lowConfidence: true,
      maxLength: 12,
    },
    { header: "ITEM\nUPC", code: "IITM2", source: PURMASTH, maxLength: 12 },
    { header: "SD", code: "ISHD2", source: PURMASTH },
    { header: "SUPL#\n3", code: "ISUPL3", source: PURMASTH },
    { header: "CNTRY", code: "ISCTR3", source: PURMASTH },
    {
      header: "SUPPLIER 3\nPRODUCT #",
      code: "ISPRD3",
      source: PURMASTH,
      maxLength: 25,
      blankMarker: "/////////////////////////",
      comment:
        ".Supplier 3 Prouduct #\n.Max length: 25 (AN)\n.Enter '/////////////////////////' (total 25 '/')             for blank value.\n.Leave this field blank if no uploaded data",
    },
    { header: "UOM", code: "ISUOM3", source: PURMASTH },
    {
      header: "UOM\nFACTOR\n5 IML DEC",
      code: "ISUFC3",
      source: PURMASTH,
      maxLength: 10,
      numberFormat: UOM_FACTOR_FORMAT,
    },
    {
      header: "MIN\nQTY",
      code: "ISMQT3",
      source: PURMASTH,
      numberFormat: WHOLE_NUMBER_FORMAT,
    },
    {
      header: "MIN\nAMT $\n2 IMP DEC",
      code: "ISMAM3",
      source: PURMASTH,
      maxLength: 10,
      numberFormat: CENTS_FORMAT,
    },
    { header: "UPC PACK 1ST Char", code: "IPAK3", source: PURMASTH },
    { header: "UPC MFTR ID 2ND Char", code: "IMID3", source: PURMASTH },
    {
      header: "SUPLR\n3 UPC",
      code: "IGLO3",
      source: PURMASTH,
      lowConfidence: true,
      maxLength: 12,
    },
    { header: "ITEM\nUPC", code: "IITM3", source: PURMASTH, maxLength: 12 },
    { header: "SD", code: "ISHD3", source: PURMASTH },
    { header: "PR STS", code: "ISTATH", source: PURMASTH },
    { header: "Y/N", code: null, key: "UPDATE_ISTATH", maxLength: 1 },
    {
      header: "SUPL 1\nQUOTE ",
      code: "ISQTE1",
      source: PURMASTH,
      // GEN-2771: alphanumeric, at most 20 (Courtney).
      maxLength: 20,
    },
    { header: "Y/N", code: null, key: "UPDATE_ISQTE1", maxLength: 1 },
    {
      header: "SUPL 2\nQUOTE ",
      code: "ISQTE2",
      source: PURMASTH,
      // GEN-2771: alphanumeric, at most 20 (Courtney).
      maxLength: 20,
    },
    { header: "Y/N", code: null, key: "UPDATE_ISQTE2", maxLength: 1 },
    {
      header: "SUPL 3\nQUOTE ",
      code: "ISQTE3",
      source: PURMASTH,
      // GEN-2771: alphanumeric, at most 20 (Courtney).
      maxLength: 20,
    },
    { header: "Y/N", code: null, key: "UPDATE_ISQTE3", maxLength: 1 },
    {
      header: "Chg Supp1 \nMinQty Y/N ",
      code: null,
      key: "CHG_ISMQT1",
      maxLength: 1,
    },
    {
      header: "Chg Supp2 \nMinQty Y/N ",
      code: null,
      key: "CHG_ISMQT2",
      maxLength: 1,
    },
    {
      header: "Chg Supp3 \nMinQty Y/N ",
      code: null,
      key: "CHG_ISMQT3",
      maxLength: 1,
    },
    {
      header: "Chg\nSupp1 \nMinAmt Y/N ",
      code: null,
      key: "CHG_ISMAM1",
      maxLength: 1,
    },
    {
      header: "Chg\nSupp2\nMinAmt Y/N ",
      code: null,
      key: "CHG_ISMAM2",
      maxLength: 1,
    },
    {
      header: "Chg\nSupp3 \nMinAmt Y/N ",
      code: null,
      key: "CHG_ISMAM3",
      maxLength: 1,
    },
    { header: "SUPL 1\nNAME ", code: "ISNAM1", source: PURMASTH },
    { header: "SUPL 2\nNAME ", code: "ISNAM2", source: PURMASTH },
    { header: "SUPL 3\nNAME", code: "ISNAM3", source: PURMASTH },
    {
      header: "PRODUCT HEIGHT-EACH          ",
      code: "IHIGEA",
      source: PURMASTD,
      maxLength: 10,
      numberFormat: DIMENSION_FORMAT,
    },
    { header: "UPDATE   Y/N", code: null, key: "UPDATE_IHIGEA", maxLength: 1 },
    {
      header: "PRODUCT WIDTH -EACH          ",
      code: "IWIDEA",
      source: PURMASTD,
      maxLength: 10,
      numberFormat: DIMENSION_FORMAT,
    },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_IWIDEA", maxLength: 1 },
    {
      header: "PRODUCT LENGTH-EACH",
      code: "ILENEA",
      source: PURMASTD,
      maxLength: 10,
      numberFormat: DIMENSION_FORMAT,
    },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_ILENEA", maxLength: 1 },
    {
      header: "PRODUCT WEIGHT-EACH",
      code: "IWHTEA",
      source: PURMASTD,
      maxLength: 10,
      numberFormat: DIMENSION_FORMAT,
    },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_IWHTEA", maxLength: 1 },
    {
      header: "PRODUCT DENSITY-EACH",
      code: "IDNSEA",
      source: PURMASTD,
      lowConfidence: true,
      maxLength: 10,
      numberFormat: DIMENSION_FORMAT,
    },
    { header: "UPDATEY/N", code: null, key: "UPDATE_IDNSEA", maxLength: 1 },
    {
      header: "PRODUCT HEIGHT-PACKAGE      ",
      code: "IHIGPG",
      source: PURMASTD,
      maxLength: 10,
      numberFormat: DIMENSION_FORMAT,
    },
    { header: "UPDATE   Y/N", code: null, key: "UPDATE_IHIGPG", maxLength: 1 },
    {
      header: "PRODUCT WIDTH-PACKAGE",
      code: "IWIDPG",
      source: PURMASTD,
      maxLength: 10,
      numberFormat: DIMENSION_FORMAT,
    },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_IWIDPG", maxLength: 1 },
    {
      header: "PRODUCT LENGTH-PACKAGE",
      code: "ILENPG",
      source: PURMASTD,
      maxLength: 10,
      numberFormat: DIMENSION_FORMAT,
    },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_ILENPG", maxLength: 1 },
    {
      header: "PRODUCT WEIGHT-PACKAGE",
      code: "IWHTPG",
      source: PURMASTD,
      maxLength: 10,
      numberFormat: DIMENSION_FORMAT,
    },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_IWHTPG", maxLength: 1 },
    {
      header: "PRODUCT DENSITY-PACKAGE",
      code: "IDNSPG",
      source: PURMASTD,
      maxLength: 10,
      numberFormat: DIMENSION_FORMAT,
    },
    { header: "UPDATEY/N", code: null, key: "UPDATE_IDNSPG", maxLength: 1 },
    {
      header: "PRODUCT HEIGHT-PALLET    ",
      code: "IHIGPT",
      source: PURMASTD,
      maxLength: 10,
      numberFormat: DIMENSION_FORMAT,
    },
    { header: "UPDATE   Y/N", code: null, key: "UPDATE_IHIGPT", maxLength: 1 },
    {
      header: "PRODUCT WIDTH-PALLET",
      code: "IWIDPT",
      source: PURMASTD,
      maxLength: 10,
      numberFormat: DIMENSION_FORMAT,
    },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_IWIDPT", maxLength: 1 },
    {
      header: "PRODUCT LENGTH-PALLET",
      code: "ILENPT",
      source: PURMASTD,
      maxLength: 10,
      numberFormat: DIMENSION_FORMAT,
    },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_ILENPT", maxLength: 1 },
    {
      header: "PRODUCT WEIGHT-PALLET",
      code: "IWHTPT",
      source: PURMASTD,
      maxLength: 10,
      numberFormat: DIMENSION_FORMAT,
    },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_IWHTPT", maxLength: 1 },
    {
      header: "PRODUCT DENSITY-PALLET",
      code: "IDNSPT",
      source: PURMASTD,
      lowConfidence: true,
      maxLength: 10,
      numberFormat: DIMENSION_FORMAT,
    },
    { header: "UPDATEY/N", code: null, key: "UPDATE_IDNSPT", maxLength: 1 },
    {
      header: "PROD WARN CODE 1",
      code: "IOWRN1",
      source: PURMASTD,
      maxLength: 3,
    },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_IOWRN1", maxLength: 1 },
    {
      header: "PROD WARN CODE 2",
      code: "IOWRN2",
      source: PURMASTD,
      maxLength: 3,
    },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_IOWRN2", maxLength: 1 },
    {
      header: "PROD WARN CODE 3",
      code: "IOWRN3",
      source: PURMASTD,
      maxLength: 3,
    },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_IOWRN3", maxLength: 1 },
    { header: "Product Remark Eng 1", code: "IOTH1E", source: PURMASTD },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_IOTH1E", maxLength: 1 },
    { header: "Product Remark Eng 2", code: "IOTH2E", source: PURMASTD },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_IOTH2E", maxLength: 1 },
    { header: "Product Remark Frn 1", code: "IOTH1F", source: PURMASTD },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_IOTH1F", maxLength: 1 },
    { header: "Product Remark Frn 2", code: "IOTH2F", source: PURMASTD },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_IOTH2F", maxLength: 1 },
  ],
  PRICE: [
    { header: "ITEMNO", code: "ITEMNO", identity: true, maxLength: 20 },
    {
      header: "2 IMPL DEC\nPCPRC1",
      code: "PCPRC1",
      maxLength: 10,
      numberFormat: CENTS_FORMAT,
    },
    {
      header: "Reset1",
      code: null,
      key: "RESET1",
      maxLength: 1,
      comment: "To Reset PCPRC1 - Select 'Y' and leave PCPRC1 blank.",
    },
    {
      header: "2 IMPL DEC\nPCPRC2",
      code: "PCPRC2",
      maxLength: 10,
      numberFormat: CENTS_FORMAT,
    },
    {
      header: "Reset2",
      code: null,
      key: "RESET2",
      maxLength: 1,
      comment: "To Reset PCPRC2 - Select 'Y' and leave PCPRC2 blank.",
    },
    {
      header: "2 IMPL DEC\nPCPRC3",
      code: "PCPRC3",
      maxLength: 10,
      numberFormat: CENTS_FORMAT,
    },
    {
      header: "Reset3",
      code: null,
      key: "RESET3",
      maxLength: 1,
      comment: "To Reset PCPRC3 - Select 'Y' and leave PCPRC3 blank.",
    },
    {
      header: "2 IMPL DEC\nPCPRC4",
      code: "PCPRC4",
      maxLength: 10,
      numberFormat: CENTS_FORMAT,
    },
    {
      header: "Reset4",
      code: null,
      key: "RESET4",
      maxLength: 1,
      comment: "To Reset PCPRC4 - Select 'Y' and leave PCPRC4 blank.",
    },
    {
      header: "2 IMPL DEC\nPCPRC5",
      code: "PCPRC5",
      maxLength: 10,
      numberFormat: CENTS_FORMAT,
    },
    {
      header: "Reset5",
      code: null,
      key: "RESET5",
      maxLength: 1,
      comment: "To Reset PCPRC5 - Select 'Y' and leave PCPRC5 blank.",
    },
    {
      header: "2 IMPL DEC\nPCPRC6",
      code: "PCPRC6",
      maxLength: 10,
      numberFormat: CENTS_FORMAT,
    },
    {
      header: "Reset6",
      code: null,
      key: "RESET6",
      maxLength: 1,
      comment: "To Reset PCPRC6 - Select 'Y' and leave PCPRC6 blank.",
    },
    {
      header: "2 IMPL DEC\nPCPRC7",
      code: "PCPRC7",
      maxLength: 10,
      numberFormat: CENTS_FORMAT,
    },
    {
      header: "Reset7",
      code: null,
      key: "RESET7",
      maxLength: 1,
      comment: "To Reset PCPRC7 - Select 'Y' and leave PCPRC7 blank.",
    },
    {
      header: "2 IMPL DEC\nPCPRC8",
      code: "PCPRC8",
      maxLength: 10,
      numberFormat: CENTS_FORMAT,
    },
    {
      header: "Reset8",
      code: null,
      key: "RESET8",
      maxLength: 1,
      comment: "To Reset PCPRC8 - Select 'Y' and leave PCPRC8 blank.",
    },
    {
      header: "2 IMPL DEC\nPCPRC9",
      code: "PCPRC9",
      maxLength: 10,
      numberFormat: CENTS_FORMAT,
    },
    {
      header: "Reset9",
      code: null,
      key: "RESET9",
      maxLength: 1,
      comment: "To Reset PCPRC9 - Select 'Y' and leave PCPRC9 blank.",
    },
  ],
  ITEM_ADD: [
    { header: "PRODUCT CODE", code: "IDITEM", identity: true, maxLength: 20 },
    // Language and line number are key fields of the itemad record (an item
    // holds one additional-description row per language + line), so a change
    // row must carry them to name which line it changes.
    { header: "L\nC", code: "IDLANG", identity: true },
    { header: "L#", code: "IDLINE", identity: true },
    { header: "ADDITION DESCRIPTION", code: "IDDESC", maxLength: 40 },
    {
      header: "CHG ADDITIONAL DESCRIPTION FLAG (Y/N/D)",
      code: null,
      key: "CHG_IDDESC",
      maxLength: 1,
      comment:
        "Change Additional Description Flag (Y/N/D or Blank)\nMax length: 1AN\nValid Value: Y, N or Blank",
    },
    { header: "", code: null },
  ],
  // STOCK DATA WORK SHEET: one row per stock record — a product at one stocking
  // location — read from `as_stock`. The location is a cell here, the only
  // worksheet where it is: AS/400 keys the record by it, so it is an identity
  // column beside the product number. It is a stocking location (`sloc`), not
  // the item-master branch the other worksheets are keyed by (see
  // {@link as400RecordBranchForTable}).
  //
  // Mapped from `stock.json` by field description where one names the column.
  // The rest — the change flags after each field and the columns with no
  // `as_stock` counterpart — are export-only, blank until the review fills
  // them. MAX IS is `seoqis` on the procurement docs' reading of
  // `sminis`/`seoqis` as min/max, hence `lowConfidence`; "Sugg by Cust" and
  // "Cust Add Date" sit where the off-season min/max would (their flags are
  // CMOSF), but their headers name something else, so they are left unmapped
  // rather than read from `sminos`/`seoqos`.
  //
  // Keys carry a `STK_` prefix because Stock Add reads the same `as_stock`
  // rows: export-only values are stored per ingest row, so the two worksheets'
  // keys (both have a CRF) must never meet.
  STOCK: [
    // A one-character code, like every AS/400 location key.
    { header: "LOC", code: "SLOC", identity: true, maxLength: 1 },
    { header: "ITEM NUMBER", code: "ITEMNO", identity: true, maxLength: 20 },
    { header: "ROW", code: "SWROW" },
    { header: "CRF", code: null, key: "STK_CRF", maxLength: 1 },
    { header: "BIN", code: "SWBIN" },
    { header: "CBF", code: null, key: "STK_CBF", maxLength: 1 },
    { header: "SHF", code: "SWSHLF" },
    { header: "CSF", code: null, key: "STK_CSF", maxLength: 1 },
    { header: "STK\nITM", code: null, key: "STK_STK_ITM" },
    { header: "IM", code: "SMANCD" },
    { header: "MC", code: "SABCTG" },
    { header: "CPF", code: null, key: "STK_CPF", maxLength: 1 },
    { header: "MINIS", code: "SMINIS", numberFormat: WHOLE_NUMBER_FORMAT },
    { header: "CMISF", code: null, key: "STK_CMISF", maxLength: 1 },
    { header: "Sugg by Cust", code: null, key: "STK_SUGG_BY_CUST" },
    { header: "CMOSF", code: null, key: "STK_CMOSF", maxLength: 1 },
    { header: "LT", code: "SXLEAD", numberFormat: WHOLE_NUMBER_FORMAT },
    { header: "CLF", code: null, key: "STK_CLF", maxLength: 1 },
    // NUMERIC(7,2): "RE-ORDER QTY IN-SEASON".
    {
      header: "MAX IS",
      code: "SEOQIS",
      lowConfidence: true,
      maxLength: 8,
      numberFormat: { integerDigits: 5, fractionDigits: 2 },
    },
    // The workbook names this flag CMISF too; keyed apart from MINIS's.
    { header: "CMISF", code: null, key: "STK_CMAXISF", maxLength: 1 },
    { header: "Cust Add Date", code: null, key: "STK_CUST_ADD_DATE" },
    // Likewise a second CMOSF.
    { header: "CMOSF", code: null, key: "STK_CMAXOSF", maxLength: 1 },
    { header: "U LVL", code: "SPCLVL" },
    { header: "CULF", code: null, key: "STK_CULF", maxLength: 1 },
    // "CUSTOMER RESTRICTED (Y/N)".
    { header: "CUST R", code: "SRESTR", maxLength: 1 },
    { header: "STATUS", code: "SSTAT" },
    { header: "CSTATF", code: null, key: "STK_CSTATF", maxLength: 1 },
    {
      header: "REP FLG",
      code: null,
      key: "STK_REP_FLG",
      maxLength: 1,
      comment:
        "ONLY Use Stock Data worksheet to 'ADD'\nReplenishment OVR flag.\nUse Stock Additional worksheet to 'Change' an existing flag.",
    },
    {
      header: "DMI Y/N",
      code: null,
      key: "STK_DMI",
      maxLength: 1,
      comment: "DMI\nMax length:  1 AN\nValid value: Y/N",
    },
    {
      header: "OVERSTOCK CONTROLLED FLAG",
      code: null,
      key: "STK_OVERSTOCK_CONTROLLED",
      maxLength: 1,
      comment: "Overstock Controlled Flag\nMax Length: 1AN\nValid Value: Y/N",
    },
    {
      header: "PRIMARY LOCATION FLAG",
      code: null,
      key: "STK_PRIMARY_LOCATION",
      maxLength: 1,
      comment: "Primary Location Flag \nMax Length: 1AN\nValid Value: Y/N/D",
    },
    {
      header: "CHANGE MAXIMUM PICK FRONT QUANTITY FLAG",
      code: null,
      key: "STK_CHG_MAX_PICK_FRONT",
      maxLength: 1,
      comment:
        "Change Maximum Pick Front Quantity\nMax Length: 1AN\nValue Value: Y/N",
    },
    // "PICK FRONT MAXIMUM QTY"; the header's mask is the note's 7.2.
    {
      header: "MAXIMUM PICK FRONT QUANTITY 99999.99",
      code: "SQTY1",
      maxLength: 8,
      comment:
        "Maximum Pick Front Quantity\nMax Length: 7.2 Numeric\nValid Value: 0 to 99999.99",
    },
    {
      header: "CHANGE CURRENT PICK FRONT QUANTITY FLAG",
      code: null,
      key: "STK_CHG_CURRENT_PICK_FRONT",
      maxLength: 1,
      comment:
        "Change Current Pick Front Quantity\nMax Length: 1AN\nValid Values: Y/N",
    },
    // "PICK FRONT CURRENT QTY".
    {
      header: "CURRENT PICK FRONT QUANTITY 99999.99",
      code: "SQTY2",
      maxLength: 8,
      comment:
        "Current Pick Front Quantity\nMax Length: 7.2 Numeric\nValid Value: 0 to 99999.99",
    },
    { header: "ALPH2 Core Offer", code: "SALPH2" },
    { header: "Update  Y/N", code: null, key: "STK_UPDATE", maxLength: 1 },
  ],
  // STOCK ADD DATA WORKS SHEET: the replenishment settings of a stock record —
  // keyed, like the stock record, by location and product, and read off the
  // same `as_stock` rows so a review can change an existing record's. None of
  // its other fields is on `as_stock` (no ingest file carries them), so they
  // are export-only: blank until the review fills them.
  //
  // `STKAD_` keys, apart from Stock's (see above). The two "2 IMPL" / "5 IMPL"
  // headers state the decimals and nothing else, so that is all their formats
  // say.
  STOCK_ADD: [
    { header: "LOC", code: "SLOC", identity: true, maxLength: 1 },
    {
      header: "PRODUCT NUMBER",
      code: "ITEMNO",
      identity: true,
      maxLength: 20,
    },
    { header: "REP\nLOC", code: null, key: "STKAD_REP_LOC" },
    { header: "CRF", code: null, key: "STKAD_CRF", maxLength: 1 },
    { header: "ORD TYP", code: null, key: "STKAD_ORD_TYP" },
    { header: "CRTYPF", code: null, key: "STKAD_CRTYPF", maxLength: 1 },
    { header: "VEND\nNUMB", code: null, key: "STKAD_VEND_NUMB" },
    { header: "CVNF", code: null, key: "STKAD_CVNF", maxLength: 1 },
    { header: "PUR UOM", code: null, key: "STKAD_PUR_UOM" },
    { header: "CUOMF", code: null, key: "STKAD_CUOMF", maxLength: 1 },
    { header: "PUR\nMIN\nQTY", code: null, key: "STKAD_PUR_MIN_QTY" },
    { header: "CMQF", code: null, key: "STKAD_CMQF", maxLength: 1 },
    {
      header: "PUR MIN\nAMT\n2 IMPL",
      code: null,
      key: "STKAD_PUR_MIN_AMT",
      numberFormat: { fractionDigits: 2 },
    },
    // The workbook names this flag CMQF too; keyed apart from MIN QTY's.
    { header: "CMQF", code: null, key: "STKAD_CMAF", maxLength: 1 },
    {
      header: "UOM FCT\n5 IMPL",
      code: null,
      key: "STKAD_UOM_FCT",
      numberFormat: { fractionDigits: 5 },
    },
    { header: "CUMF", code: null, key: "STKAD_CUMF", maxLength: 1 },
    { header: "SHP DIR", code: null, key: "STKAD_SHP_DIR" },
    {
      header: "RPL FLG",
      code: null,
      key: "STKAD_RPL_FLG",
      maxLength: 1,
      comment: "Use Stock Additional worksheet to 'Change' and existing flag.",
    },
  ],
  // The whole worksheet: four columns, no change flag of its own, no spacer.
  //
  // Every code here is a **proposal**, not a match — there is no keyword file on
  // the ingest mirrors and no AS/400 field listing for one, so nothing confirms
  // them (see {@link AS400_EXPORT_GROUPS_WITHOUT_INGEST_TABLE}). They follow the
  // Item Add convention, which is the file's two-letter prefix plus the field's
  // role: `itemad` spells them `IDITEM`/`IDLANG`/`IDLINE`/`IDDESC`, so an
  // `itemkw` file spells them `IK…` with `IKKEYW` for the keyword itself. The
  // item file's own `IALPH1..3` keywords are a different thing — three 10-char
  // fields on the item record, not this file's 50-char per-language lines.
  //
  // Lengths and notes are the worksheet's own cell comments, which state every
  // one of the four (unlike Item Add, whose `IDLANG`/`IDLINE` carry no note and
  // so no limit).
  ITEM_KW: [
    {
      header: "PRODUCT CODE",
      code: "IKITEM",
      identity: true,
      lowConfidence: true,
      maxLength: 20,
      comment:
        "Product code:\nMax length: 20 AN\nCan not be blank and Must be a valid Syntax Item Number.",
    },
    // Language and line number are key fields of the keyword record (an item
    // holds one keyword row per language + line, exactly as it holds one
    // additional-description row per language + line), so a change row must
    // carry them to name which line it changes.
    {
      header: "L\nC",
      code: "IKLANG",
      identity: true,
      lowConfidence: true,
      maxLength: 1,
      comment:
        "Language code:\nMax length: 1AN\nCan be: 'E' = English Key Words\n              'F' = French Key Words",
    },
    {
      header: "L#",
      code: "IKLINE",
      identity: true,
      lowConfidence: true,
      maxLength: 2,
      comment: "Line number:\nMax length: 2 N\nCan be: 1 ~ 15.",
    },
    {
      header: "Web Product Keywords",
      code: "IKKEYW",
      lowConfidence: true,
      maxLength: 50,
      blankMarker: "/////////////////////////",
      comment:
        "Web Product Keyword\nMax length: 50 AN\nEnter '/////////////////////////' for blank value.\nNote: leave this field blank or enter '/' as blank value will remove keyword from item file.",
    },
  ],
  MATRIX_CLASSIFICATION: [
    { header: "PRODUCT", code: "IMXITEM", identity: true, maxLength: 20 },
    {
      header: "MATRIX CLASS",
      code: "IMXCLASS",
      maxLength: 5,
      comment: "Character 5",
    },
    {
      header: "MATRIX CLASS UPDATE FLAG",
      code: "IMXMCCHG",
      maxLength: 1,
      comment:
        "If Y then Matrix Class will be updated with value from column B",
    },
    {
      header: "SENSITIVITY SCORE",
      code: "IMXSCOR",
      maxLength: 6,
      comment: "Numeric 5 (2 decimals)",
    },
    {
      header: "SENSITIVITY SCORE UPDATE FLAG",
      code: null,
      key: "UPDATE_IMXSCOR",
      maxLength: 1,
      comment:
        "If Y Sensitivity Score will be updated with value from column D",
    },
    {
      header: "CEILING MARGIN %",
      code: "IMXMGCL",
      maxLength: 6,
      comment: "Numeric 5 (2 decimals)",
    },
    {
      header: "Reset1",
      code: null,
      key: "RESET1",
      maxLength: 1,
      comment:
        "To clear Ceiling Margin % select 'Y' and leave Ceiling Margin % field blank.",
    },
    {
      header: "TARGET MARGIN %",
      code: "IMXMGTG",
      maxLength: 6,
      comment: "Numeric 5 (2 decimals)",
    },
    {
      header: "Reset2",
      code: null,
      key: "RESET2",
      maxLength: 1,
      comment:
        "To clear Target Margin % select 'Y' and leave Target Margin % field blank.",
    },
    {
      header: "FLOOR MARGIN %",
      code: "IMXMGFL",
      maxLength: 6,
      comment: "Numeric 5 (2 decimals)",
    },
    {
      header: "Reset3",
      code: null,
      key: "RESET3",
      maxLength: 1,
      comment:
        "To clear Floor Margin % select 'Y' and leave Floor  Margin % field blank.",
    },
    {
      header: "CEILING PRICE  (LEVEL 4)",
      code: "IMXPRCL",
      maxLength: 12,
      numberFormat: MATRIX_PRICE_FORMAT,
      comment: "Numeric 11 (4 decimals)",
    },
    {
      header: "Reset4",
      code: null,
      key: "RESET4",
      maxLength: 1,
      comment:
        "To clear Ceiling Price Level 4 select 'Y' and leave Ceiling Price Level 4 field blank.",
    },
    {
      header: "TARGET PRICE  (LEVEL 3)",
      code: "IMXPRTG",
      maxLength: 12,
      numberFormat: MATRIX_PRICE_FORMAT,
      comment: "Numeric 11 (4 decimals)",
    },
    {
      header: "Reset5",
      code: null,
      key: "RESET5",
      maxLength: 1,
      comment:
        "To clear Target Price Level 3 select 'Y' and leave Target Price Level 3 field blank.",
    },
    {
      header: "FLOOR PRICE  (LEVEL 2)",
      code: "IMXPRFL",
      maxLength: 12,
      numberFormat: MATRIX_PRICE_FORMAT,
      comment: "Numeric 11 (4 decimals)",
    },
    {
      header: "Reset6",
      code: null,
      key: "RESET6",
      maxLength: 1,
      comment:
        "To clear Floor Price Level 2 select 'Y' and leave Floor Price Level 2 field blank.",
    },
    {
      header: "CEILING DISCOUNT %",
      code: "IMXDPCL",
      maxLength: 6,
      comment: "Numeric 5 (2 decimals)",
    },
    {
      header: "Reset7",
      code: null,
      key: "RESET7",
      maxLength: 1,
      comment:
        "To clear Ceiling Discount % select 'Y' and leave Ceiling Discount % field blank.",
    },
    {
      header: "TARGET DISCOUNT %",
      code: "IMXDPTG",
      maxLength: 6,
      comment: "Numeric 5 (2 decimals)",
    },
    {
      header: "Reset8",
      code: null,
      key: "RESET8",
      maxLength: 1,
      comment:
        "To clear Target Discount % select 'Y' and leave Target Discount % field blank",
    },
    {
      header: "FLOOR DISCOUNT %",
      code: "IMXDPFL",
      maxLength: 6,
      comment: "Numeric 5 (2 decimals)",
    },
    {
      header: "Reset9",
      code: null,
      key: "RESET9",
      maxLength: 1,
      comment:
        "To clear Floor Discount % select 'Y' and leave Floor Discount % field blank",
    },
    {
      header: "CEILING DISCOUNT MULTIPLIER",
      code: "IMXDMCL",
      maxLength: 8,
      comment: "Numeric 7 (4 decimals)",
    },
    {
      header: "Reset10",
      code: null,
      key: "RESET10",
      maxLength: 1,
      comment:
        "To clear Ceiling Discount Multiplier select 'Y' and leave Ceiling Discount Multiplier field blank",
    },
    {
      header: "TARGET DISCOUNT MULTIPLIER",
      code: "IMXDMTG",
      maxLength: 8,
      comment: "Numeric 7 (4 decimals)",
    },
    {
      header: "Reset11",
      code: null,
      key: "RESET11",
      maxLength: 1,
      comment:
        "To clear Target Discount Multiplier select 'Y' and leave Target Discount Multiplier field blank",
    },
    {
      header: "FLOOR DISCOUNT MULTIPLIER",
      code: "IMXDMFL",
      maxLength: 8,
      comment: "Numeric 7 (4 decimals)",
    },
    {
      header: "Reset12",
      code: null,
      key: "RESET12",
      maxLength: 1,
      comment:
        "To clear Floor Discount Multiplier select 'Y' and leave Floor Discount Multiplier field blank",
    },
    {
      header: "UPDATE MATRIX PRICE FLAG",
      code: "IMXUPDF",
      maxLength: 1,
      comment: "Valid values Y/N",
    },
    {
      header: "ALLOW PRICE ROUNDUP FLAG",
      code: "IMXRNDF",
      maxLength: 1,
      comment: "Valid values Y/N",
    },
    {
      header: "WEB PRICE LINE",
      code: "IMXPRLN",
      maxLength: 5,
      comment: "Character 5",
    },
  ],
};

/**
 * The field codes of a group's identity columns
 * ({@link As400ExportColumn.identity}) — the cells a change-mode upload row
 * always carries so the AS/400 can find the record, while every other
 * unchanged cell is emitted blank ("do not change").
 */
export function as400IdentityColumnCodes(group: As400ExportGroup): Set<string> {
  const codes = new Set<string>();
  for (const column of AS400_EXPORT_WORKSHEET_COLUMNS[group]) {
    if (column.code && column.identity) codes.add(column.code);
  }
  return codes;
}

/**
 * Field-code pairs the AS/400 only accepts together: a change upload that
 * changes one half without the other is rejected. Product group / sub group
 * (PG/SG) and price class / sub class (PC/SC) are each one classification
 * split over two 2-character fields.
 */
export const AS400_EXPORT_PAIRED_COLUMNS: readonly (readonly [
  string,
  string,
])[] = [
  ["IGROUP", "ISGRUP"],
  ["ICLASS", "ISCLAS"],
];

/**
 * One of the Purchase Data worksheet's three supplier sections (GEN-2764):
 * the supplier's columns, numbered 1–3, and the Ship Direct (SD) flag that
 * closes them. The AS/400 rejects a section whose SD isn't Y and deletes
 * everything entered to the left of it, so the flag must be Y whenever the
 * section carries data — on adds and changes alike.
 */
export interface As400SupplierSection {
  number: 1 | 2 | 3;
  /** The section's data: every column left of its SD flag. */
  dataCodes: readonly string[];
  /** Its Ship Direct flag (`ISHD<n>`). */
  shipDirectCode: string;
}

// A supplier section's columns, in worksheet order, without their number.
const SUPPLIER_SECTION_FIELDS = [
  "ISUPL",
  "ISCTR",
  "ISPRD",
  "ISUOM",
  "ISUFC",
  "ISMQT",
  "ISMAM",
  "IPAK",
  "IMID",
  "IGLO",
  "IITM",
] as const;

export const AS400_SUPPLIER_SECTIONS: readonly As400SupplierSection[] = (
  [1, 2, 3] as const
).map((number) => ({
  number,
  dataCodes: SUPPLIER_SECTION_FIELDS.map((field) => `${field}${number}`),
  shipDirectCode: `ISHD${number}`,
}));

// The per-supplier columns that sit right of the three sections, numbered the
// same way: the quote and its Y/N, the Chg MinQty / MinAmt flags, the name.
const SUPPLIER_TRAILING_FIELDS = [
  "ISQTE",
  "UPDATE_ISQTE",
  "CHG_ISMQT",
  "CHG_ISMAM",
  "ISNAM",
] as const;

const SUPPLIER_NUMBER_BY_EDIT_KEY: ReadonlyMap<string, 1 | 2 | 3> = new Map(
  AS400_SUPPLIER_SECTIONS.flatMap((section) =>
    [
      ...section.dataCodes,
      section.shipDirectCode,
      ...SUPPLIER_TRAILING_FIELDS.map((field) => `${field}${section.number}`),
    ].map((editKey): [string, 1 | 2 | 3] => [editKey, section.number]),
  ),
);

/**
 * Which supplier (1–3) a column belongs to, by its edit key
 * ({@link as400ColumnEditKey}): a section's columns and its SD flag, and the
 * supplier's quote, name and change flags further right. Undefined for a
 * column that is no one supplier's. The review grid and the worksheet export
 * mark the suppliers' columns by it (GEN-2778), since the Purchase Data sheet
 * is wide enough that data has been typed into the wrong supplier's fields.
 */
export function as400SupplierNumberOf(
  editKey: string | null,
): 1 | 2 | 3 | undefined {
  return editKey === null
    ? undefined
    : SUPPLIER_NUMBER_BY_EDIT_KEY.get(editKey);
}

/**
 * Each supplier's colour: `solid` for the grid's section dividers and band,
 * `tint` (opaque, light) for header fills where no alpha is available — the
 * exported worksheet's header cells. Teal, violet and fuchsia stay clear of
 * the review grid's cell tints (amber pending, green staged, red before, blue
 * carried), which already mean something.
 */
export const AS400_SUPPLIER_COLORS: Readonly<
  Record<1 | 2 | 3, { solid: string; tint: string }>
> = {
  1: { solid: "#14B8A6", tint: "#CCFBF1" },
  2: { solid: "#8B5CF6", tint: "#EDE9FE" },
  3: { solid: "#D946EF", tint: "#FAE8FF" },
};

/**
 * Field code → the codes whose change makes a change-mode upload row carry it,
 * at its current value, though it is itself unchanged:
 * - each half of an {@link AS400_EXPORT_PAIRED_COLUMNS} pair, when the other
 *   half changes — the AS/400 rejects a change to one half alone;
 * - a supplier section's Ship Direct flag, when any of the section's data
 *   changes — a flag already Y would otherwise go out blank and the AS/400
 *   would drop the section ({@link As400SupplierSection}). One way only: a
 *   changed flag carries none of the section.
 */
export const AS400_EXPORT_CARRIED_WITH: ReadonlyMap<string, readonly string[]> =
  new Map<string, readonly string[]>([
    ...AS400_EXPORT_PAIRED_COLUMNS.flatMap(
      ([first, second]): [string, readonly string[]][] => [
        [first, [second]],
        [second, [first]],
      ],
    ),
    ...AS400_SUPPLIER_SECTIONS.map((section): [string, readonly string[]] => [
      section.shipDirectCode,
      section.dataCodes,
    ]),
  ]);

/**
 * The field codes a change-mode upload row must carry, given the codes the
 * review actually changes on it: those codes plus every code
 * {@link AS400_EXPORT_CARRIED_WITH} brings along with one of them. A carried
 * code rides along with its current value — it is exported, not edited.
 */
export function withAs400CarriedColumns(
  changedCodes: ReadonlySet<string>,
): Set<string> {
  const carried = new Set(changedCodes);
  for (const [code, triggers] of AS400_EXPORT_CARRIED_WITH) {
    if (triggers.some((trigger) => changedCodes.has(trigger))) {
      carried.add(code);
    }
  }
  return carried;
}

/**
 * Edit key → the column's character limit, for those of the given columns that
 * declare one ({@link As400ExportColumn.maxLength}). Keyed the way cells are
 * addressed ({@link as400ColumnEditKey}), so a caller holding a cell's key can
 * look its limit up directly. A key that is absent has no stated limit — see
 * the field's own note on why that is "unknown", not "unlimited".
 */
export function as400ColumnMaxLengths(
  columns: readonly As400ExportColumn[],
): ReadonlyMap<string, number> {
  const limits = new Map<string, number>();
  for (const column of columns) {
    const editKey = as400ColumnEditKey(column);
    if (editKey && column.maxLength !== undefined) {
      limits.set(editKey, column.maxLength);
    }
  }
  return limits;
}

// A header mask: a run of 9s, optionally a point and another run, standing
// alone in the header (`SHP'G MULT 99999`, `WEIGHT 9999.999`). At least two
// 9s: a lone 9 is a number in the name (`USER DEF 9`, a one-character text
// field), and no worksheet states a one-digit mask.
const HEADER_NUMBER_MASK = /(?:^|\s)(9{2,})(?:\.(9+))?(?=\s|$)/;

/** The number format a header's own mask states, if it has one. */
export function as400HeaderNumberFormat(
  header: string,
): As400NumberFormat | undefined {
  const match = HEADER_NUMBER_MASK.exec(header);
  if (!match?.[1]) return undefined;
  return {
    integerDigits: match[1].length,
    fractionDigits: match[2]?.length ?? 0,
  };
}

/**
 * Edit key → the number format its cells must follow: the column's explicit
 * {@link As400ExportColumn.numberFormat}, else the mask in its header. A key
 * that is absent is not numeric, as far as the workbook says.
 */
export function as400ColumnNumberFormats(
  columns: readonly As400ExportColumn[],
): ReadonlyMap<string, As400NumberFormat> {
  const formats = new Map<string, As400NumberFormat>();
  for (const column of columns) {
    const editKey = as400ColumnEditKey(column);
    const format =
      column.numberFormat ?? as400HeaderNumberFormat(column.header);
    if (editKey && format) formats.set(editKey, format);
  }
  return formats;
}

/**
 * The edit keys of the columns whose spaces are data (see
 * {@link As400ExportColumn.keepWhitespace}).
 */
export function as400WhitespaceSignificantKeys(
  columns: readonly As400ExportColumn[],
): ReadonlySet<string> {
  const keys = new Set<string>();
  for (const column of columns) {
    const editKey = as400ColumnEditKey(column);
    if (editKey && column.keepWhitespace) keys.add(editKey);
  }
  return keys;
}

/**
 * Characters folded to an ASCII equivalent on their way to the AS/400, which
 * accepts nothing else. Unicode NFKD already handles most of what shows up in
 * text pasted from Excel or Word — accented letters (É → E + a combining acute
 * the fold then drops), the non-breaking and thin spaces, `…` → `...`, `™` → `TM`
 * — so this table is only what NFKD leaves alone: the Latin ligatures and
 * stroked letters that decompose to nothing, the typographic quotes, dashes,
 * and inch/foot primes vendor size text uses, and the invisibles (zero-width
 * characters, the soft hyphen, and the line breaks a multi-line spreadsheet cell
 * carries, which no single-line upload record can hold).
 *
 * Deliberately not here: characters with no ASCII spelling at all — `°`, `©`,
 * `±`, `•`, currency signs, anything non-Latin. Guessing at those means either
 * dropping them (`90°C` → `90C`) or inventing text, and which one a description
 * wants is the author's call, so {@link foldToAs400Ascii} leaves them in place
 * for the caller to refuse instead. See {@link nonAs400AsciiCharacters}.
 */
const AS400_ASCII_FOLDS: ReadonlyMap<string, string> = new Map([
  // Ligatures and stroked letters NFKD does not decompose.
  ["Æ", "AE"],
  ["æ", "ae"],
  ["Œ", "OE"],
  ["œ", "oe"],
  ["ß", "ss"],
  ["Ø", "O"],
  ["ø", "o"],
  ["Ð", "D"],
  ["ð", "d"],
  ["Đ", "D"],
  ["đ", "d"],
  ["Þ", "TH"],
  ["þ", "th"],
  ["Ł", "L"],
  ["ł", "l"],
  // Typographic punctuation.
  ["‘", "'"],
  ["’", "'"],
  ["‚", "'"],
  ["‛", "'"],
  ["ʼ", "'"],
  ["‹", "'"],
  ["›", "'"],
  ["“", '"'],
  ["”", '"'],
  ["„", '"'],
  ["‟", '"'],
  ["«", '"'],
  ["»", '"'],
  // Inch / foot primes from vendor size text (Rack-a-Tiers `(26″ – 30″)`).
  // Escapes so they are not mistaken for ASCII quotes in source.
  ["\u2032", "'"], // prime
  ["\u2033", '"'], // double prime
  ["‐", "-"],
  ["‑", "-"],
  ["‒", "-"],
  ["–", "-"],
  ["—", "-"],
  ["―", "-"],
  ["−", "-"],
  // Invisibles: line breaks and tabs collapse to a space, the rest vanish.
  // Spelled as escapes — a literal zero-width character in source is invisible
  // to the next reader and to any tool that reformats this file.
  ["\t", " "],
  ["\n", " "],
  ["\r", " "],
  ["\u00AD", ""], // soft hyphen
  ["\u200B", ""], // zero-width space
  ["\u200C", ""], // zero-width non-joiner
  ["\u200D", ""], // zero-width joiner
  ["\uFEFF", ""], // byte-order mark
]);

/** Combining marks, left behind by the NFKD decomposition of accented letters. */
const COMBINING_MARKS = /[\u0300-\u036F]/gu;

/**
 * Rewrite a cell value as the AS/400 spelling of itself: the folds above, then
 * an NFKD decomposition with the combining marks dropped, so `TÉLÉPHONE` becomes
 * `TELEPHONE`.
 *
 * Not guaranteed to return ASCII — a character with no ASCII equivalent is left
 * as it is, on purpose (see {@link AS400_ASCII_FOLDS}). Callers ask
 * {@link nonAs400AsciiCharacters} what survived and refuse the value rather than
 * silently dropping part of it.
 */
export function foldToAs400Ascii(value: string): string {
  const folded = Array.from(value)
    .map((character) => AS400_ASCII_FOLDS.get(character) ?? character)
    .join("");
  return folded.normalize("NFKD").replace(COMBINING_MARKS, "");
}

/**
 * Anything outside printable ASCII — the AS/400 upload takes `0x20`–`0x7E` and
 * nothing else, so control characters are as unusable as a `°`. Code-point aware
 * (`u`), so an astral character is reported as itself rather than as two lone
 * surrogates.
 */
const NON_AS400_ASCII = /[^\x20-\x7E]/gu;

/** The distinct unusable characters in a value, in the order they appear. */
export function nonAs400AsciiCharacters(value: string): string[] {
  return [...new Set(value.match(NON_AS400_ASCII) ?? [])];
}

/**
 * Whether a value is printable ASCII throughout, as the AS/400 requires. Asked
 * through `match`, not `NON_AS400_ASCII.test`: a `/g` regex carries `lastIndex`
 * between calls, so testing it would answer every other call wrongly.
 */
export function isAs400Ascii(value: string): boolean {
  return value.match(NON_AS400_ASCII) === null;
}

/**
 * Import-path sanitizer for text that will be loaded onto the AS/400.
 *
 * Policy (GEN-2567 / Rexel IM Weekly 2026-09-16):
 * 1. Upper-case. AS/400 item descriptions and codes are caps; lowercase from
 *    vendor price files corrupts into funky characters and breaks EDI POs.
 * 2. Fold known Unicode to ASCII via {@link foldToAs400Ascii} (accents,
 *    `™` → `TM`, smart quotes, ligatures).
 * 3. Strip whatever is still outside printable ASCII (`®`, `©`, `°`, `±`,
 *    CJK, …). Silent strip is correct on import: authors cannot toast-fix a
 *    1000-row price file.
 *
 * The interactive review grid keeps fold-then-reject instead
 * ({@link foldToAs400Ascii} + {@link As400CellValueSchema}): an author sitting
 * on one cell can respell `90°C` as `90C` or `90 DEG C`. Do not swap that
 * path to this helper.
 *
 * Do not run this on free-text that never goes to the AS/400 (pricelist notes,
 * column headers, sheet names) or on stock-data / gains-constraint **branch
 * code** fields, which must keep mixed case (Westburn Ontario `L` vs `l`).
 * Those upload paths do not exist yet; the exception is reserved.
 */
export function sanitizeAs400Text(value: string): string {
  return foldToAs400Ascii(value.toUpperCase()).replace(NON_AS400_ASCII, "");
}

const PRICE_NUMBER_FIELD_CODE =
  /^(PCPRC[1-9]|I[BLC]CST|IMXPR(?:CL|TG|FL)|ISMAM[1-3])$/i;

export function isPriceNumberFieldCode(code: string): boolean {
  return PRICE_NUMBER_FIELD_CODE.test(code);
}

/**
 * A cell value staged onto a review, which the AS/400 upload file carries
 * verbatim. Refused rather than folded server-side: the review page folds what
 * folds ({@link foldToAs400Ascii}) as the value is typed or pasted, so the grid
 * shows what will be saved, and anything reaching here unfoldable is a value no
 * one has decided the ASCII spelling of.
 */
export const As400CellValueSchema = z.string().superRefine((value, ctx) => {
  const unusable = nonAs400AsciiCharacters(value);
  if (unusable.length === 0) return;
  ctx.addIssue({
    code: "custom",
    message: `The AS/400 accepts ASCII only — remove ${unusable.join(" ")}`,
  });
});

/**
 * Ordered, de-duplicated AS/400 field codes a group sources data from — the
 * columns the flattened query actually SELECTs. The first is the join/sort
 * key, the group's item number ({@link AS400_EXPORT_GROUP_SEARCH_COLUMN}); the
 * rest follow in worksheet order. That is worksheet order outright everywhere
 * the product number is the first column — all but Stock and Stock Add, which
 * lead with the location. Unmapped worksheet columns are excluded.
 */
export function as400ExportColumnCodes(group: As400ExportGroup): string[] {
  const itemColumn = AS400_EXPORT_GROUP_SEARCH_COLUMN[group];
  const seen = new Set<string>([itemColumn]);
  const codes: string[] = [itemColumn];
  for (const { code } of AS400_EXPORT_WORKSHEET_COLUMNS[group]) {
    if (code && !seen.has(code)) {
      seen.add(code);
      codes.push(code);
    }
  }
  return codes;
}

/**
 * Field code → the ingest table-name suffix declared to own it
 * ({@link As400ExportColumn.source}). Codes without a declaration (all of a
 * single-file-type group's) are absent — the group's one file type owns them.
 */
export function as400ExportColumnSources(
  group: As400ExportGroup,
): ReadonlyMap<string, string> {
  const bySuffix = new Map<string, string>();
  for (const { code, source } of AS400_EXPORT_WORKSHEET_COLUMNS[group]) {
    if (code && source) bySuffix.set(code, source);
  }
  return bySuffix;
}

/**
 * The column each AS/400 view is searched by — its item number, which is the
 * SKU the AS/400 keys the record on. The review page's search box is a
 * shorthand for a `contains` filter on this column, so the term is evaluated by
 * the ingest server (a predicate on the mirror) rather than app-side over a
 * fetched page.
 *
 * It is `ITEMNO` in most views; the exceptions are tables that name the item
 * column differently (the Item Add and Matrix Classification worksheets, and
 * the GAINS cost / associated-items / quantities views).
 */
export const AS400_EXPORT_GROUP_SEARCH_COLUMN: Record<
  As400ExportGroup,
  string
> = {
  ITEM: "ITEMNO",
  PURCHASE_DATA: "ITEMNO",
  PRICE: "ITEMNO",
  ITEM_ADD: "IDITEM",
  STOCK: "ITEMNO",
  STOCK_ADD: "ITEMNO",
  ITEM_KW: "IKITEM",
  MATRIX_CLASSIFICATION: "IMXITEM",
};

/**
 * The worksheets that carry a record's location as a cell, and which column:
 * the stock files, keyed by stocking location as well as by product. Every
 * other worksheet's location travels in the file name (the item file's) and
 * the record's key, never in a cell.
 */
export const AS400_EXPORT_GROUP_LOCATION_COLUMN: Partial<
  Record<As400ExportGroup, string>
> = {
  STOCK: "SLOC",
  STOCK_ADD: "SLOC",
};

export const AS400_VIEW_GROUP_SEARCH_COLUMN: Record<As400ViewGroup, string> = {
  ITEMMAST5: "ITEMNO",
  ADDITIONAL_DESCRIPTION: "ITEMNO",
  GAINS_COST: "PRODUCT",
  ASSOCIATED_ITEMS: "ASPROD",
  QUANTITIES: "ITEM NO",
};

// No total count here: the exact COUNT(*) can be far slower than the page
// query (trigram search, FDW metadata filters), so it must not block first
// draw. Clients fetch it separately via `countCatalog` and render "of many"
// until it resolves; `hasNextPage` (from a pageSize+1 fetch) keeps prev/next
// paging working in the meantime.
export const ListItemCatalogResponseSchema = z.object({
  edges: z.array(ItemCatalogItemSchema),
  pageInfo: z.object({
    currentPage: z.number(),
    hasNextPage: z.boolean(),
    hasPreviousPage: z.boolean(),
    /**
     * Present on the ingest-scan path: the token that opens the next page (null
     * once the scan is exhausted). Absent when the page came from the plain
     * product query, which is addressable by number.
     */
    nextCursor: z.string().nullable().optional(),
  }),
});

// Exact count of the catalog rows matching the same filters as `listCatalog`
// (page/pageSize and sortDirection don't affect the count and are omitted).
export const CountItemCatalogInputSchema = ItemCatalogFilterSchema.omit({
  sortDirection: true,
})
  .extend({
    // Advanced filters on ProductV3's own columns only. Filters on raw ingest
    // columns are deliberately not accepted: those are resolved by walking the
    // ingest tables (see `cursor`), and counting every product they match would
    // mean walking all of them — so a filtered catalog pages without a total
    // and the client simply doesn't ask for one.
    advancedFilters: z
      .array(CatalogBaseAdvancedFilterSchema)
      .max(20)
      .optional(),
    // Same change-set scope as listCatalog.
    changeSetId: z.string().uuid().optional(),
    // Same item-list scope as listCatalog.
    itemListId: z.string().uuid().optional(),
    // Same Source scope as listCatalog — served by the product query, so the
    // count stays exact under it. (Locations are not accepted: they put the
    // catalog on the ingest scan, whose count is countCatalogScan.)
    dataSourceIds: catalogRecordKeyFields.dataSourceIds,
  })
  .superRefine(refineCatalogSearch);

export const CountItemCatalogResponseSchema = z.object({
  totalCount: z.number().int(),
});

// ─── Counting the ingest-scan path ───────────────────────────────────────────

/**
 * How many distinct products the scan-path count walks before giving up on an
 * exact answer. The count runs the same resumable walk as the page query, so an
 * unselective filter set (millions of matches) would otherwise turn "show me a
 * number" into a full catalog scan; past this many the pager's open-ended
 * "of many" is as informative as any number would be.
 */
export const CATALOG_SCAN_COUNT_CAP = 10_000;

/**
 * Distinct-product count of a catalog view the ingest scan serves — the path
 * `countCatalog` deliberately refuses (a raw-column filter, or a Source /
 * Location scope). Takes the full filter set in the same shape as
 * `listCatalog`, and answers by running the same walk the pages come from, so
 * the number always agrees with the rows: product-level matching, base-column
 * filters, change-set and item-list scope all behave identically.
 *
 * The walk is capped ({@link CATALOG_SCAN_COUNT_CAP} products) and time-boxed
 * server-side, so the answer may be a floor rather than a total — `exact`
 * says which. Requests that don't need the scan are rejected: `countCatalog`
 * answers those exactly and more cheaply.
 */
export const CountItemCatalogScanInputSchema = ItemCatalogFilterSchema.omit({
  sortDirection: true,
})
  .extend({
    advancedFilters: z.array(CatalogAdvancedFilterSchema).max(20).optional(),
    ...catalogFilterMatchField,
    ...catalogRecordKeyFields,
    changeSetId: z.string().uuid().optional(),
    itemListId: z.string().uuid().optional(),
  })
  .superRefine(refineCatalogSearch);

export const CountItemCatalogScanResponseSchema = z.object({
  /** Distinct matching products found before the walk ended or was cut off. */
  count: z.number().int().nonnegative(),
  /**
   * True when the walk was exhausted — `count` is the total. False when the
   * cap or the time budget cut it off first — `count` is a floor, and the
   * client keeps its open-ended wording.
   */
  exact: z.boolean(),
});

// ─── Item-number resolution ──────────────────────────────────────────────────
// Translates a pasted list of identifiers into the org's products. `field`
// picks which identifier the values are:
//
// - `itemNumber` — the Rexel item number (AS/400 ITEMNO, shown as
//   `displaySku`), matched trimmed + uppercased.
// - `supplierPartNumber` — any of a product's three supplier product numbers
//   (PURMASTH ISPRD1/2/3), matched trimmed + uppercased.
// - `upc` — the product's UPC (the `upc` schema extension), matched trimmed and
//   otherwise exactly: leading zeroes are part of the code.
//
// None of these is unique, so one input can match several products (multiple
// keys/divisions/suppliers) — every match is returned, one entry per (input,
// product) pair, and inputs that match nothing come back in `unmatchedInputs`
// so no loss is silent. Every match carries the product's `displaySku`, so a
// set pasted by supplier part number lands on Rexel item numbers like any other.
export const CATALOG_IDENTIFIER_FIELDS = [
  "itemNumber",
  "supplierPartNumber",
  "upc",
] as const;
export const CatalogIdentifierFieldSchema = z.enum(CATALOG_IDENTIFIER_FIELDS);
export type CatalogIdentifierField = z.infer<
  typeof CatalogIdentifierFieldSchema
>;

/**
 * The most values one resolve accepts, per field. Item numbers are matched on
 * im_products_v3 itself (an indexed expression lookup) and the item-list flow
 * resolves whole lists that way. The other identifiers are looked up in the
 * ingest / extension tables, where the catalog's own 10,000-value filter cap is
 * the ceiling.
 */
export const MAX_IDENTIFIER_VALUES_BY_FIELD: Record<
  CatalogIdentifierField,
  number
> = {
  itemNumber: 50000,
  supplierPartNumber: 10000,
  upc: 10000,
};

// `itemNumbers` keeps its name for API compatibility: it carries the pasted
// values whichever `field` they are.
export const ResolveItemNumbersInputSchema = z
  .object({
    field: CatalogIdentifierFieldSchema.default("itemNumber"),
    itemNumbers: z.array(z.string().trim().min(1)).min(1).max(50000),
  })
  .superRefine((input, ctx) => {
    const max = MAX_IDENTIFIER_VALUES_BY_FIELD[input.field];
    if (input.itemNumbers.length > max) {
      ctx.addIssue({
        code: "custom",
        path: ["itemNumbers"],
        message: `At most ${max} values can be resolved by ${input.field} at once.`,
      });
    }
  });

export const ResolveItemNumbersResponseSchema = z.object({
  matched: z.array(
    z.object({
      // The raw input that matched — duplicate spellings are folded onto the
      // first one seen.
      input: z.string(),
      // ProductV3.key — the product's explicit string primary key.
      productKey: z.string(),
      // The product's Rexel item number. A product with no item number is
      // never a match, whatever `field` was searched.
      displaySku: z.string(),
    }),
  ),
  unmatchedInputs: z.array(z.string()),
});

export const MAX_UPC_LOOKUP_INPUTS = 200;

export const UpcCatalogMatchSchema = z.object({
  productKey: z.string(),
  partNumber: z.string(),
  divisions: z.array(z.string()),
  isActive: z.boolean(),
  isSellable: z.boolean(),
});

export const LookupByUpcInputSchema = z.object({
  upcs: z.array(z.string()).max(MAX_UPC_LOOKUP_INPUTS),
});

export const LookupByUpcResponseSchema = z.object({
  byUpc: z.record(z.string(), z.array(UpcCatalogMatchSchema)),
});

export const ListRexelItemsInputSchema = z.object({
  baseSku: z.string(),
});

export const InitinfcRecordSchema = z.object({
  id: z.string().uuid(),
  division: z.string(),
  fileVariant: z.string(),
  giitem: z.string(),
  giloc: z.string(),
  gidept: z.string().nullable(),
  gigroup: z.string().nullable(),
  gidstlvl: z.string().nullable(),
  gidescr: z.string().nullable(),
  giselprc: z.string().nullable(),
  gistdcst: z.string().nullable(),
  gifobcst: z.string().nullable(),
  giwght: z.string().nullable(),
  gicnvfct: z.string().nullable(),
  gimaxqty: z.string().nullable(),
  giminqty: z.string().nullable(),
  giincord: z.string().nullable(),
  gilead: z.string().nullable(),
  gisrvlvl: z.string().nullable(),
  gishlife: z.string().nullable(),
  gircvcst: z.string().nullable(),
  gicrycst: z.string().nullable(),
  giprcbrk: z.string().nullable(),
  gifrtbrk: z.string().nullable(),
  gimodel: z.string().nullable(),
  giinvcls: z.string().nullable(),
  gistkflg: z.string().nullable(),
  gionhand: z.number().int(),
  giunshpq: z.number().int(),
  gistrdat: z.string().nullable(),
  giparent1: z.string().nullable(),
  gipercent1: z.string().nullable(),
  giparent2: z.string().nullable(),
  gipercent2: z.string().nullable(),
  gimaxtyp: z.string().nullable(),
  gimaxval: z.string().nullable(),
  gissmin: z.string().nullable(),
  giuom: z.string().nullable(),
  gitype: z.string().nullable(),
  girsq: z.string().nullable(),
  girsqapl: z.string().nullable(),
  gilifetyp: z.string().nullable(),
  gibuyer: z.string().nullable(),
  gichar1: z.string().nullable(),
  gichar2: z.string().nullable(),
  gichar3: z.string().nullable(),
  gichar4: z.string().nullable(),
  ginum1: z.string().nullable(),
  ginum2: z.string().nullable(),
  ginum3: z.string().nullable(),
  ginum4: z.string().nullable(),
  gieor: z.string().nullable(),
});

export const ListRexelItemsResponseSchema = z.object({
  edges: z.array(InitinfcRecordSchema),
});

// ─── Contract ───────────────────────────────────────────────────────────────

export const ListPriceListLinesInputSchema = z.object({
  productId: z.string().uuid(),
});

export const PriceListLineItemSchema = z.object({
  id: z.string().uuid(),
  priceListId: z.string().uuid().nullable(),
  priceListName: z.string().nullable(),
  unitPrice: z.string(),
  uomCode: z.string().nullable(),
  pricingMethod: z.string().nullable(),
  effectiveFrom: z.string().nullable(),
  effectiveTo: z.string().nullable(),
});

export const ListPriceListLinesResponseSchema = z.object({
  edges: z.array(PriceListLineItemSchema),
});

export const ListProductRawDataInputSchema = z.object({
  // ProductV3.key — the product's explicit string primary key.
  productKey: z.string(),
  /**
   * The catalog's active advanced filters. When any of them targets a raw
   * metadata column, the linked records are narrowed to the ones that actually
   * satisfy the filter set: only ingest tables carrying *every* filtered column
   * are read, and only rows meeting *all* of the filters (AND) are returned —
   * so the expanded row shows the records that matched, not every record the
   * product happens to link. Base ProductV3 filters are ignored here (they
   * constrain which products are listed, not which of their records match).
   *
   * Under `advancedFilterMatch: "product"` the narrowing is dropped entirely:
   * the product qualified through records in several source files, so "the
   * records that matched" is no longer one readable answer — the expanded row
   * shows every record the product links, which is the point of treating them
   * as extensions of the one item.
   */
  advancedFilters: z.array(CatalogAdvancedFilterSchema).max(20).optional(),
  ...catalogFilterMatchField,
  // The catalog's Source and Location filters, narrowing the linked records the
  // same way: a record outside the scope is one of the reasons this product is
  // *not* in the list, so showing it under a product that is would misread as
  // the filter having been ignored.
  ...catalogRecordKeyFields,
});

// A change set's staged field edits (field name → new value). Present only on
// change-set-scoped reads; null when nothing is staged for the record/row.
const StagedValuesSchema = z
  .record(z.string(), z.unknown())
  .nullable()
  .optional();

// One linked raw ingest row for a product. `row` is the raw record's columns
// (arbitrary per ingest table); null when the row can't be read (table dropped
// or ingest DB unavailable). `fieldDescriptions` maps each raw column name
// (upper-cased) to the description of the SchemaFieldDefinition that produced
// it, for surfacing as column-header tooltips — empty when the linked schema's
// fields carry no descriptions.
export const ProductRawDataRecordSchema = z.object({
  productKey: z.string(),
  // The im_product_v3_data_links row this record was resolved through.
  dataLinkId: z.string().uuid(),
  dataSourceLinkId: z.string().uuid(),
  dataSourceName: z.string().nullable(),
  dataSourceGroupName: z.string().nullable(),
  // Identity of the shared schema definition (`core_schema_definitions`) this
  // link conforms to. Many data sources map their own physical foreign table
  // (e.g. `qe_as_item`, `qo_as_item`) onto the same schema definition
  // (`as_item`); the client groups same-definition rows into one table by id.
  schemaDefinitionId: z.string().uuid(),
  schemaDefinitionName: z.string(),
  schemaName: z.string(),
  tableName: z.string(),
  dataId: z.string().uuid(),
  row: z.record(z.string(), z.unknown()).nullable(),
  fieldDescriptions: z.record(z.string(), z.string().nullable()),
});

export const ListProductRawDataResponseSchema = z.object({
  edges: z.array(ProductRawDataRecordSchema),
});

// A distinct raw metadata column available for advanced filtering, sourced
// from the schema field definitions of the org's linked ingest tables — an
// app-DB-only lookup, no ingest rows are read.
export const CatalogMetadataColumnSchema = z.object({
  name: z.string(),
  description: z.string().nullable(),
});

export const ListCatalogMetadataColumnsResponseSchema = z.object({
  columns: z.array(CatalogMetadataColumnSchema),
});

// A division (DataSource) that participates in the item catalog — it has at
// least one linked ingest table the catalog can scan. The Source filter and
// the AS/400 view division selectors offer exactly these, NOT every
// DataSource the org has: other feeds keep their own per-division sources
// (e.g. the GAINS ingest repeats the same division titles), which would
// duplicate every division label with a button no product can ever match.
export const CatalogDivisionSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
});

export const ListCatalogDivisionsResponseSchema = z.object({
  divisions: z.array(CatalogDivisionSchema),
});

export const As400GridSortDirectionSchema = z.enum(["asc", "desc"]);
export type As400GridSortDirection = z.infer<
  typeof As400GridSortDirectionSchema
>;

/**
 * Whether `column` names a column of worksheet `group`: one of its edit keys
 * ({@link as400ColumnEditKey}), or the CDB key column every worksheet shows.
 */
export function isAs400WorksheetColumnKey(
  group: As400ExportGroup,
  column: string,
): boolean {
  return (
    column === AS400_CDB_COLUMN ||
    AS400_EXPORT_WORKSHEET_COLUMNS[group].some(
      (worksheetColumn) => as400ColumnEditKey(worksheetColumn) === column,
    )
  );
}

/**
 * A review-wide column sort (GEN-2714): one worksheet's column, by its edit key
 * (or the CDB key column), in one direction. Set once for the review and sent
 * with every worksheet's page — a worksheet without the column still follows
 * the order it gives, matching rows by record (product + CDB key).
 */
export const As400GridSortSchema = z
  .object({
    group: As400ExportGroupSchema,
    column: z.string().min(1).max(100),
    direction: As400GridSortDirectionSchema,
  })
  .refine(({ group, column }) => isAs400WorksheetColumnKey(group, column), {
    message: "Sort column is not a column of that worksheet",
  });
export type As400GridSort = z.infer<typeof As400GridSortSchema>;

/**
 * The sorted column as worksheet `group` has it, or null when it has none —
 * `columnKeys` being the worksheet's column keys. The CDB key and the item
 * number exist on every worksheet (the item number under each worksheet's own
 * column); any other column only where the worksheet has it. A worksheet with
 * the column sorts by its own values in it; one without follows the sorted
 * worksheet's records.
 */
export function as400SortColumnOn(
  sort: As400GridSort,
  group: As400ExportGroup,
  columnKeys: readonly (string | null)[],
): string | null {
  if (sort.column === AS400_CDB_COLUMN) return AS400_CDB_COLUMN;
  if (sort.column === AS400_EXPORT_GROUP_SEARCH_COLUMN[sort.group]) {
    return AS400_EXPORT_GROUP_SEARCH_COLUMN[group];
  }
  return columnKeys.includes(sort.column) ? sort.column : null;
}

// ─── AS/400 export flattened view ────────────────────────────────────────────
// A paginated, flattened view of the ingest metadata records behind one AS/400
// export file format, restricted to one change set's products (the review
// detail page — the only place these worksheets are shown). Paging runs over
// the change set's membership table (exact totals); the page's cells come from
// the FDW mirrors of the matched ingest tables (the selected DataSource's links
// whose table name ends in the group's suffix, e.g. `_as_price`), read by
// surrogate id. The queried columns are `as400ExportColumnCodes(group)`.
// PURCHASE_DATA joins its two file types (purmasth + purmastd) on `itemno`.
export const ListAs400ExportInputSchema = z.object({
  group: As400ExportGroupSchema,
  // The divisions to read from: one or more DataSources. Only their links'
  // ingest tables (matched by the group's suffix) are queried. Passing several
  // combines them into one flattened view (each division's rows concatenated),
  // so the on-screen table can show the divisions together.
  dataSourceIds: z.array(z.string().uuid()).min(1).max(50),
  // The page number: the view pages the change set's local membership table.
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(200).default(50),
  // Advanced filters on the view's own columns, evaluated by the ingest server
  // (a pushed-down predicate on the mirror, not an app-side pass over fetched
  // rows). A product is kept when one of its ingest rows in this view satisfies
  // every filter. Filters referencing a column not in the group match nothing.
  advancedFilters: z.array(CatalogAdvancedFilterSchema).max(20).optional(),
  // The Location filter: a row is kept when its location code, or its whole
  // company/division/branch key, is one of these — the same two readings the
  // catalog's Location filter matches (see `buildLocationCondition`). Applied
  // per row alongside the advanced filters; omitted = every location.
  locations: z.array(CatalogLocationSchema).max(50).optional(),
  // The change set whose products the view covers. Rows carry `stagedValues`
  // and the pageInfo carries exact totals.
  changeSetId: z.string().uuid(),
  // The review's column sort; omitted = the canonical item-then-CDB order.
  sort: As400GridSortSchema.optional(),
});

// One ingest source row behind an export row: which data-source link's table it
// came from and the surrogate `id` of the row there. A single-table export row
// has one source; a joined view (Purchase Data) has up to two.
export const As400ExportRowSourceSchema = z.object({
  dataSourceLinkId: z.string().uuid(),
  dataId: z.string().uuid(),
});

export const As400ExportRowSchema = z.object({
  // Stable identity for row selection: the row's source (linkId,dataId) pairs
  // joined. Unique per row across pages so selection survives paging.
  key: z.string(),
  // The ingest source rows this export row is built from (1, or 2 when joined).
  sources: z.array(As400ExportRowSourceSchema),
  // Displayed cell values keyed by canonical field code; text or null.
  values: z.record(z.string(), z.string().nullable()),
  // Staged field edits (field code → new value) from the requested change set,
  // merged across the row's sources.
  stagedValues: StagedValuesSchema,
  // Each staged ingest field's drift baseline: the live value captured when the
  // field was first edited in the change set. Saving the field back to this
  // value reverts it (the staged edit is dropped), which is what lets the grid
  // put a saved cell back even after the live value has drifted. Keyed like
  // `stagedValues`; export-only columns have no baseline (blank reverts them).
  stagedOriginalValues: z
    .record(z.string(), z.string().nullable())
    .nullable()
    .optional(),
});

// A net-new worksheet row authored on a change set: a product the AS/400
// doesn't have yet, staged so the upload file can create it. `values` is
// keyed by the worksheet column's edit key (`as400ColumnEditKey`).
export const ChangeSetNewRowSchema = z.object({
  id: z.string().uuid(),
  group: As400ExportGroupSchema,
  // The division (DataSource) whose upload file carries the row.
  dataSourceId: z.string().uuid(),
  // The location the row is added at, for a division whose upload runs per
  // branch (RD); null = the whole division, every branch its records live under.
  branch: z.string().nullable(),
  values: z.record(z.string(), z.string().nullable()),
  createdAt: z.string(),
});
export type ChangeSetNewRow = z.infer<typeof ChangeSetNewRowSchema>;

export const ListAs400ExportResponseSchema = z.object({
  // Ordered canonical field codes present in each row (matches
  // as400ExportColumnCodes(group)). Row keys; not the display headers — the
  // client renders worksheet headers from AS400_EXPORT_WORKSHEET_COLUMNS.
  columns: z.array(z.string()),
  // Each row: selection identity + its ingest sources + the value record.
  rows: z.array(As400ExportRowSchema),
  // The change set's new rows for this worksheet + division scope, pinned
  // ahead of the ingest-backed rows. Returned on page 1 with no advanced
  // filters active — filters are evaluated by the ingest server, which these
  // rows never reached, so rather than half-applying a predicate they hide
  // behind `hiddenNewRowCount` (change-set scope only).
  newRows: z.array(ChangeSetNewRowSchema).optional(),
  hiddenNewRowCount: z.number().int().optional(),
  // No total count in the unscoped view: an exact count(*) over the
  // multi-million-row FDW union is prohibitively slow, so paging is
  // cursor-style (prev/next via hasNextPage). The change-set-scoped view pages
  // over a local membership table, so it carries exact totals.
  pageInfo: z.object({
    currentPage: z.number(),
    hasNextPage: z.boolean(),
    hasPreviousPage: z.boolean(),
    totalCount: z.number().int().optional(),
    totalPages: z.number().int().optional(),
    /**
     * False when `totalCount` is a floor rather than the true total — the
     * change set was too large to sweep in one request. The pager then falls
     * back to next/previous.
     */
    totalCountExact: z.boolean().optional(),
    /** Cursor path only (the item catalog's AS/400 view) — see `cursor`. */
    nextCursor: z.string().nullable().optional(),
  }),
});

// ─── Where one ingest row sits in a review's worksheet ─────────────────────────
// Going to a cell (a comment's) means opening the page the row is on under the
// grid's own filters, Location filter and sort — which only the server knows,
// since it filters on staged values and sorts across the whole review.
export const LocateAs400ExportRowInputSchema = ListAs400ExportInputSchema.omit({
  page: true,
}).extend({
  // The ingest row to find. A joined Purchase Data row is found by either of
  // its sources.
  row: As400ExportRowSourceSchema,
});
export type LocateAs400ExportRowInput = z.input<
  typeof LocateAs400ExportRowInputSchema
>;

export const LocateAs400ExportRowResponseSchema = z.object({
  // Null when the view leaves the row out: the filters, the Location filter or
  // the divisions, or its product is no longer in the review.
  location: z
    .object({
      page: z.number().int().min(1),
      // The grid row's key (`As400ExportRow.key`), joined sources and all.
      rowKey: z.string(),
    })
    .nullable(),
});
export type LocateAs400ExportRowResponse = z.infer<
  typeof LocateAs400ExportRowResponseSchema
>;

// ─── Which divisions a review's worksheet actually has records for ───────────
// The review page offers every division the org has, but a change set usually
// touches a handful — the rest render an empty table. This answers, per
// division, whether the worksheet has anything there, so the ones with nothing
// can be greyed out rather than clicked into.
export const As400ExportDivisionCountsInputSchema = z.object({
  group: As400ExportGroupSchema,
  // Every division the selector offers — not the selected ones. The answer
  // decides what is selectable, so it cannot be scoped by the selection.
  dataSourceIds: z.array(z.string().uuid()).min(1).max(50),
  changeSetId: z.string().uuid(),
});

export const As400ExportDivisionCountsResponseSchema = z.object({
  divisions: z.array(
    z.object({
      dataSourceId: z.string().uuid(),
      /**
       * The change set's products that this division has a record for in this
       * worksheet's file types. Not the grid's row count: one product can hold
       * several records in a division (one per branch), so this is a floor —
       * enough to answer "is there anything here?", which is what it is for.
       */
      productCount: z.number().int().nonnegative(),
      /** Authored new rows for the division — records with no ingest row yet. */
      newRowCount: z.number().int().nonnegative(),
    }),
  ),
});

/** One AS/400 location key: a single upper-case letter or digit. */
export const As400BranchCodeSchema = z.string().regex(/^[0-9A-Z]$/);

// ─── AS/400 view flattened over the catalog filter's data links ──────────────
// The "AS/400 View" grid: the flattened raw data links of the products matching
// the catalog filter (free-text search + advanced filters, and the change set
// when one is in view), narrowed to one column group (ITEMMAST5,
// ADDITIONAL_DESCRIPTION, GAINS_COST, ASSOCIATED_ITEMS, QUANTITIES). One row
// per (product × linked raw record); columns are the group's fields. Paging is
// by product — on the Item Catalog's Search tab in lockstep with the Gently
// list (exact total from `countCatalog`); on the review page's Queries views
// on its own. Rows carry `stagedValues` and are editable in change-set scope;
// the item-catalog view is read-only.
//
// `advancedFilters` scope the *products* (a product is kept when one of its
// linked ingest rows satisfies every filter), evaluated by the ingest server as
// a pushed-down predicate on the mirrors. The review page's search box is one
// such filter, on AS400_VIEW_GROUP_SEARCH_COLUMN[group]; `search` is the
// catalog's own SKU/description text search, which only the Search tab uses.
export const ListCatalogAs400ViewInputSchema = ItemCatalogFilterSchema.extend({
  group: As400ViewGroupSchema,
  // `dataSourceIds` (the review page's division selector, and the catalog's own
  // Source filter) comes in with the page fields — see catalogRecordKeyFields.
  ...catalogPageFields,
  // Catalog scope only: just these products' records (the catalog's ticked or
  // pasted items), or just the item list's. Unlike `listCatalog`, these compose
  // with raw-column filters — the scan narrows its SKU column by them.
  keys: catalogKeysField.optional(),
  itemListId: z.string().uuid().optional(),
}).superRefine(refineCatalogSearch);

// ─── Streaming exports ───────────────────────────────────────────────────────
// The catalog page's "Export as…" downloads. Both exports are unbounded — they
// cover the whole filtered view, not a page — so the procedures resolve to a
// byte stream rather than a materialized value: oRPC pipes a `ReadableStream`
// output straight to the HTTP response, so the backend keeps at most a batch of
// rows (or, for a multi-division archive, one division's file) in memory and
// the browser writes the download to disk as it arrives.

export const ExportFileFormatSchema = z.enum(["csv", "xlsx"]);
export type ExportFileFormat = z.infer<typeof ExportFileFormatSchema>;

/**
 * Which delimited-text dialect an export writes.
 *
 * `excel` is comma-separated RFC 4180 with a UTF-8 BOM: a cell holding the
 * comma, a quote or a line break is wrapped in quotes, and one Excel would read
 * as a formula is prefixed with an apostrophe. It opens cleanly by double-click,
 * at the cost of a value that isn't literally what the cell holds.
 *
 * `tab` is tab-separated with no quoting at all — every cell is its exact text,
 * and the reader splits each line on the tab. This is the shape AS/400 sends its
 * data in, and the shape its upload expects back: a comma or an inch mark in a
 * description is ordinary text, so nothing has to be escaped or removed to keep
 * the columns lined up. A tab or a line break inside a value would still break
 * the row, but AS/400's own fields hold neither.
 */
export const CsvDialectSchema = z.enum(["excel", "tab"]);
export type CsvDialect = z.infer<typeof CsvDialectSchema>;

/**
 * The download's bytes. Not a JSON value: the handler returns the serialized
 * (and, for CSV, gzipped) file stream and sets the `Content-Type` /
 * `Content-Disposition` headers itself. Clients trigger these procedures as a
 * browser navigation to the RPC GET URL so the download streams to disk with
 * native progress, rather than calling them through the typed client.
 *
 * Kept as a named alias rather than its own `z.custom` so every streaming
 * download in the codebase shares one definition.
 */
export const ExportFileStreamSchema = FileStreamSchema;

// Mirrors the Search tab's on-screen state: the catalog filter plus the AS/400
// column narrowing. `as400Groups` (multi-select "AS/400 View" groups) and
// `as400ExportGroup` (single-select export file format) are mutually exclusive
// in the UI; if both arrive the export format wins.
export const ExportItemCatalogInputSchema = z
  .object({
    search: z.string().trim().optional(),
    searchMode: z.enum(["text", "regex"]).default("text"),
    isSellable: z.boolean().optional(),
    isActive: z.boolean().optional(),
    as400Groups: z.array(As400ViewGroupSchema).optional(),
    as400ExportGroup: As400ExportGroupSchema.optional(),
    // Same advanced filters and key scope (Source / Location) the on-screen view
    // applies, so the download covers exactly the rows the screen shows.
    advancedFilters: z.array(CatalogAdvancedFilterSchema).max(20).optional(),
    ...catalogFilterMatchField,
    ...catalogRecordKeyFields,
    // Same explicit-set scopes as listCatalog: a saved item list, ticked
    // catalog rows, or the product keys a pasted item-number set resolved to.
    // Omitted, the export covers the whole filtered catalog — which is why a
    // list-scoped or selection-scoped view must send one of these, or the
    // download is the unfiltered catalog.
    //
    // Unlike listCatalog, `keys` MAY ride with Source / Location / raw-column
    // filters: keys select the products, and those filters only narrow which
    // linked ingest records land in the file.
    itemListId: z.string().uuid().optional(),
    keys: catalogKeysField.optional(),
    // Restrict to one change set's products (the review page's Queries views).
    changeSetId: z.string().uuid().optional(),
    format: ExportFileFormatSchema.default("csv"),
  })
  .superRefine(refineCatalogSearch);

/**
 * Which AS/400 run an upload file is for: `ADD` (`I1`) creates records,
 * `CHANGE` (`I2`) changes existing ones. Different jobs, so a file is only ever
 * one of them.
 */
export const As400UploadModeSchema = z.enum(["ADD", "CHANGE"]);
export type As400UploadMode = z.infer<typeof As400UploadModeSchema>;

/**
 * The reviewer's override of which run a review's upload files are for.
 * Absent — the default — decides per row, as always: authored new rows go out
 * as adds, changed existing records as changes, each in files of their own.
 * Set, every row goes out in that one mode, so the review's files are all
 * `I1` or all `I2`. A changed record forced to `ADD` carries every value it
 * has, not only the changed ones: the add run reads a blank cell as the
 * record's value, not as "leave this alone".
 */
export const As400UploadModeOverrideSchema = As400UploadModeSchema.optional();

// The review page's flattened AS/400 worksheet for one export file format,
// scoped to the selected division(s) and change set. `advancedFilters` are
// pushed down to the ingest mirrors, so the download covers exactly the rows
// the on-screen view shows.
export const ExportAs400ViewInputSchema = z.object({
  group: As400ExportGroupSchema,
  // The export is one file per division and per mode — the AS/400 upload runs
  // one mode per file, so a division's authored new rows (adds) and its edits to
  // existing records (changes) are always separate files. Anything but a lone
  // file downloads as a zip.
  dataSourceIds: z.array(z.string().uuid()).min(1).max(50),
  advancedFilters: z.array(CatalogAdvancedFilterSchema).max(20).optional(),
  // The on-screen Location filter, so the file matches the grid — see
  // ListAs400ExportInputSchema.
  locations: z.array(CatalogLocationSchema).max(50).optional(),
  changeSetId: z.string().uuid(),
  format: ExportFileFormatSchema.default("csv"),
  /**
   * Which delimited-text dialect the CSV format writes
   * ({@link CsvDialectSchema}): Excel's quoted CSV by default, or `tab` for the
   * unquoted tab-separated file AS/400 exchanges. Ignored by the XLSX format,
   * whose cells carry no delimiter to escape around.
   */
  csvDialect: CsvDialectSchema.default("excel"),
  /** Force every file to `I1` or `I2` ({@link As400UploadModeOverrideSchema}). */
  modeOverride: As400UploadModeOverrideSchema,
});

/**
 * The same view as {@link ExportAs400ViewInputSchema}, asked whether it would
 * produce a file with anything in it. No `format` — the answer doesn't depend
 * on one — and no `csvDialect`, which only decides how a cell already in the
 * file is written.
 */
export const CountAs400ExportRowsInputSchema = ExportAs400ViewInputSchema.omit({
  format: true,
  csvDialect: true,
});

export const As400ExportHasRowsResponseSchema = z.object({
  /**
   * True when at least one planned file has a row: an existing record the
   * change set changes, or an authored new row. False means there is nothing to
   * upload, so the page says so instead of downloading a header-only file.
   */
  hasRows: z.boolean(),
});

export const productsContracts = {
  listCatalog: oc
    .input(ListItemCatalogInputSchema)
    .output(ListItemCatalogResponseSchema)
    .route({
      method: "GET",
      summary: "Search the item catalog",
      description:
        "Paged catalog products by text or regex search, sellable/active flags, advanced column filters, division, location, item list, review or explicit keys. No total; count separately.",
    }),
  countCatalog: oc
    .input(CountItemCatalogInputSchema)
    .output(CountItemCatalogResponseSchema)
    .route({
      method: "GET",
      summary: "Count item catalog products",
      description:
        "Returns the exact number of catalog products matching search, flags, product-column filters, review or item list. Not for raw-column, division or location filters.",
    }),
  countCatalogScan: oc
    .input(CountItemCatalogScanInputSchema)
    .output(CountItemCatalogScanResponseSchema)
    .route({
      method: "GET",
      summary: "Count catalog products for raw-data filters",
      description:
        "Counts products matching a catalog view that filters on raw source columns, division or location; capped at 10,000 and time-boxed, so it may return a floor.",
    }),
  resolveItemNumbers: oc
    .input(ResolveItemNumbersInputSchema)
    .output(ResolveItemNumbersResponseSchema)
    .route({
      method: "POST",
      summary: "Look up products by item number, supplier part number or UPC",
      description:
        "Matches a pasted list of item numbers (up to 50,000), supplier part numbers or UPCs (up to 10,000) to catalog products; returns every match per input and the inputs that matched nothing.",
    }),
  lookupByUpc: oc
    .input(LookupByUpcInputSchema)
    .output(LookupByUpcResponseSchema)
    .route({
      method: "POST",
      summary: "Look up products by UPC",
      description:
        "For up to 200 UPC codes, returns the catalog products carrying each exact UPC, with part number, divisions and active and sellable flags.",
    }),
  listAs400Export: oc
    .input(ListAs400ExportInputSchema)
    .output(ListAs400ExportResponseSchema)
    .route({
      method: "GET",
      summary: "List a review's ERP upload worksheet rows",
      description:
        "Paged rows of one ERP item-master upload file format for a product review's products across chosen divisions, with staged edits, new rows, filters, location and sort.",
    }),
  locateAs400ExportRow: oc
    .input(LocateAs400ExportRowInputSchema)
    .output(LocateAs400ExportRowResponseSchema)
    .route({
      method: "GET",
      summary: "Find a row's page in a review worksheet",
      description:
        "For one ERP source row in a product review's upload worksheet, returns the page that shows it under the given divisions, filters, location and sort, or null when they hide it.",
    }),
  as400ExportDivisionCounts: oc
    .input(As400ExportDivisionCountsInputSchema)
    .output(As400ExportDivisionCountsResponseSchema)
    .route({
      method: "GET",
      summary: "Count review worksheet records per division",
      description:
        "For one ERP upload worksheet in a product review, returns per division how many of the review's products have records and how many new rows were authored.",
    }),
  listCatalogAs400View: oc
    .input(ListCatalogAs400ViewInputSchema)
    .output(ListAs400ExportResponseSchema)
    .route({
      method: "GET",
      summary: "View catalog products' ERP source records",
      description:
        "Paged ERP source records (item master, descriptions, costs, associated items, quantities) for products matching the catalog filters, one column group at a time.",
    }),
  listRexelItems: oc
    .input(ListRexelItemsInputSchema)
    .output(ListRexelItemsResponseSchema)
    .route({
      method: "GET",
      summary: "List planning item records for a SKU",
      description:
        "Returns the per-division, per-location forecasting item records for a base SKU: cost, price, min/max, lead time, service level, on-hand, source and buyer fields.",
    }),
  listPriceListLines: oc
    .input(ListPriceListLinesInputSchema)
    .output(ListPriceListLinesResponseSchema)
    .route({
      method: "GET",
      summary: "List price list lines for a product",
      description:
        "Returns every price list line for one product: price list, unit price, unit of measure, pricing method and effective dates.",
    }),
  listRawDataRecords: oc
    .input(ListProductRawDataInputSchema)
    .output(ListProductRawDataResponseSchema)
    .route({
      method: "GET",
      summary: "List a product's raw source records",
      description:
        "Returns the raw ingested source rows linked to one product, with data source and column descriptions, narrowed by the catalog's raw-column, division and location filters.",
    }),
  listMetadataColumns: oc
    .input(z.object({}))
    .output(ListCatalogMetadataColumnsResponseSchema)
    .route({
      method: "GET",
      summary: "List catalog filter columns",
      description:
        "Returns the distinct raw source-data columns, with descriptions, that can be used as advanced filters in the item catalog.",
    }),
  listCatalogDivisions: oc
    .input(z.object({}))
    .output(ListCatalogDivisionsResponseSchema)
    .route({
      method: "GET",
      summary: "List item catalog divisions",
      description:
        "Returns the divisions (data sources) with linked item data in the catalog, for the Source filter and division selectors.",
    }),
  exportCatalog: oc
    .input(ExportItemCatalogInputSchema)
    .output(ExportFileStreamSchema)
    .route({
      method: "GET",
      summary: "Export the item catalog",
      description:
        "Downloads the whole filtered item catalog as CSV or Excel, honoring search, advanced filters, division, location, item list, selected keys or review, optionally with ERP column groups.",
    }),
  exportAs400View: oc
    .input(ExportAs400ViewInputSchema)
    .output(ExportFileStreamSchema)
    .route({
      method: "GET",
      summary: "Download a review's ERP upload files",
      description:
        "Downloads one ERP item-master upload worksheet for a product review as CSV (Excel or tab) or Excel; multiple divisions or adds and changes come as a zip.",
    }),
  as400ExportHasRows: oc
    .input(CountAs400ExportRowsInputSchema)
    .output(As400ExportHasRowsResponseSchema)
    .route({
      method: "GET",
      summary: "Check if an ERP upload file has rows",
      description:
        "Reports whether a product review's ERP upload worksheet export would contain any changed or new rows, checked before starting the download.",
    }),
} as const;

// ─── Type exports ───────────────────────────────────────────────────────────

export type ItemCatalogItem = z.infer<typeof ItemCatalogItemSchema>;
export type ItemCatalogFilter = z.infer<typeof ItemCatalogFilterSchema>;
export type ListItemCatalogInput = z.infer<typeof ListItemCatalogInputSchema>;
export type ListItemCatalogResponse = z.infer<
  typeof ListItemCatalogResponseSchema
>;
export type CountItemCatalogInput = z.infer<typeof CountItemCatalogInputSchema>;
export type CountItemCatalogResponse = z.infer<
  typeof CountItemCatalogResponseSchema
>;
export type CountItemCatalogScanInput = z.infer<
  typeof CountItemCatalogScanInputSchema
>;
export type CountItemCatalogScanResponse = z.infer<
  typeof CountItemCatalogScanResponseSchema
>;
export type ResolveItemNumbersInput = z.infer<
  typeof ResolveItemNumbersInputSchema
>;
export type ResolveItemNumbersResponse = z.infer<
  typeof ResolveItemNumbersResponseSchema
>;
export type UpcCatalogMatch = z.infer<typeof UpcCatalogMatchSchema>;
export type LookupByUpcInput = z.infer<typeof LookupByUpcInputSchema>;
export type LookupByUpcResponse = z.infer<typeof LookupByUpcResponseSchema>;
export type ListAs400ExportInput = z.infer<typeof ListAs400ExportInputSchema>;
export type ListCatalogAs400ViewInput = z.infer<
  typeof ListCatalogAs400ViewInputSchema
>;
export type ListAs400ExportResponse = z.infer<
  typeof ListAs400ExportResponseSchema
>;
export type As400ExportRow = z.infer<typeof As400ExportRowSchema>;
export type As400ExportRowSource = z.infer<typeof As400ExportRowSourceSchema>;
export type As400ExportDivisionCountsInput = z.infer<
  typeof As400ExportDivisionCountsInputSchema
>;
export type As400ExportDivisionCountsResponse = z.infer<
  typeof As400ExportDivisionCountsResponseSchema
>;
export type InitinfcRecord = z.infer<typeof InitinfcRecordSchema>;
export type ListRexelItemsInput = z.infer<typeof ListRexelItemsInputSchema>;
export type ListRexelItemsResponse = z.infer<
  typeof ListRexelItemsResponseSchema
>;
export type PriceListLineItem = z.infer<typeof PriceListLineItemSchema>;
export type ListPriceListLinesInput = z.infer<
  typeof ListPriceListLinesInputSchema
>;
export type ListPriceListLinesResponse = z.infer<
  typeof ListPriceListLinesResponseSchema
>;
export type ProductRawDataRecord = z.infer<typeof ProductRawDataRecordSchema>;
export type ListProductRawDataInput = z.infer<
  typeof ListProductRawDataInputSchema
>;
export type ListProductRawDataResponse = z.infer<
  typeof ListProductRawDataResponseSchema
>;
export type CatalogMetadataColumn = z.infer<typeof CatalogMetadataColumnSchema>;
export type ListCatalogMetadataColumnsResponse = z.infer<
  typeof ListCatalogMetadataColumnsResponseSchema
>;
export type CatalogDivision = z.infer<typeof CatalogDivisionSchema>;
export type ListCatalogDivisionsResponse = z.infer<
  typeof ListCatalogDivisionsResponseSchema
>;
export type ExportItemCatalogInput = z.input<
  typeof ExportItemCatalogInputSchema
>;
export type ExportAs400ViewInput = z.input<typeof ExportAs400ViewInputSchema>;
export type CountAs400ExportRowsInput = z.input<
  typeof CountAs400ExportRowsInputSchema
>;
export type As400ExportHasRowsResponse = z.infer<
  typeof As400ExportHasRowsResponseSchema
>;
